import React, {useEffect, useMemo, useRef, useState} from 'react';
import JsSIP from 'jssip';
import {Button} from "primereact/button";
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import PhoneKeyboard from "./PhoneKeyboard";
import {Toast} from "primereact/toast";
import {CSSTransition} from 'react-transition-group';
import {callOptions, DEV, probeTurnServers, STAGE, Version, WEBRTC_WSS_URL, WSS_URL, WWS_WEBRTC} from "../../redux/config";
import {useEffectiveOnlineStatus} from "../../utils/internetReachability";
import {getCallDetailIdFromData, getCallIdForCallEvent, getGroupSessionId, getSessionFlowUuid} from "../../utils/callSessionUtils";
import {recordPinnedUserConsole} from "../../utils/userConsoleLogs";
import {logAction, logCall, logHub, logLogout, logLogoutCancelled, logLogoutPending, restoreUserConsoleLogsToStorage} from "../../utils/appEventLogs";
// TEMP disabled for deploy: background-tab hub fix. Restore import from "../../utils/hubConnectionPolicy".
import {flushPendingErrorReports, getUsersWebRTC, reportCriticalError, sendCallEvents} from "../../redux/api/apiWebRTC";
import {AutoComplete} from "primereact/autocomplete";
import {ConfirmDialog} from "primereact/confirmdialog";
import {checkSessionForSpy, checkUserForCall} from "../../redux/api/api";
import {attachHangupHeaderToEarlyCancel} from "./hangupMarker";
import {attachLocalSdpCandidateFilter} from "./selectedIceCandidate";
import {
    changeSession,
    getGlobalJsSIP,
    initGlobalJsSIP,
    registerHubConnection,
    // TEMP disabled for deploy: retireHubConnection,
    setGlobalCall,
    setGlobalConsultationCall,
    setGlobalIncomingSession,
    setGlobalPendingIncomingSession,
    stopGlobalJsSIP,
    unregisterHubConnection
} from "./GlobalJsSIP";
import {
    checkSipConnection,
    clearRecallSession,
    sessionReconnected,
    setActiveSession,
    setErrorJsSip,
    setErrorJsSipTab,
    setMonitoringHubError,
    setReconnectSession,
    setSpySessionId,
    setSpyCallAnsweredTick,
    setSipUserBusy,
    setStatusHubError,
    setTranscriptionHubStatus,
    setUserDataHubError
} from "../../redux/actions/actionsWebRTC";
import {TabPanel, TabView} from 'primereact/tabview';
import MonitoringSipDetails from "../Monitoring/MonitoringList/MonitoringSipDetails";
import {getEventMonitorToUserDetail} from "../../redux/api/apiMonitoringList";
import * as signalR from "@microsoft/signalr";
import {HubConnectionBuilder} from "@microsoft/signalr";
import {DecodedToken} from "../../types/types";
import {jwtDecode} from "jwt-decode";
import {
    clearMonitoringSipDetails,
    getNewMonitoringQueue,
    removeFromMonitoringQueue,
    setQueueMonitorStats
} from "../../redux/actions/actionsMonitoringList";
import {GET_CONSULTATION_FOR_SIP, GET_MONITORING_FOR_SIP,} from "../../redux/types/typesMonitoringList";
import ConsultationSipDetails from "../Monitoring/MonitoringList/ConsultationSipDetails";
import {
    SET_UPLOAD_FILE_STATUS_SUCCESS,
    SET_USER_STATUS_ERROR,
    SET_USER_STATUS_SUCCESS
} from "../../redux/types/typesUsersList";
import {showStatisticsExportLoader, updateFilesHistoryFromHub} from "../../redux/actions/actionsStatisticsList";
import {clearAllStates} from "../../redux/actions/actions";
import {setRolesVisible} from "../../redux/actions/actionsRoles";
import {ERROR_ACCOUNT_WEB_LOGIN} from "../../redux/types/types";
import {useNavigate} from "react-router-dom";
import "./JsSip.scss";

interface NetworkInformation extends EventTarget {
    downlink: number;
    effectiveType: string;
}

const CRITICAL_ERROR_KEY_PREFIX = "webrtc_critical_error_sent_";

function shouldReportCriticalError(shortCode: string): boolean {
    if (typeof window === "undefined" || !window.localStorage) return true;
    try {
        const key = CRITICAL_ERROR_KEY_PREFIX + shortCode;
        if (window.localStorage.getItem(key)) {
            return false;
        }
        window.localStorage.setItem(key, "1");
        return true;
    } catch {
        return true;
    }
}

function clearCriticalErrorFlags(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith(CRITICAL_ERROR_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    } catch {
    }
}

function parseTurnAudioString(audio: unknown): {
    echoCancellation: boolean;
    autoGainControl: boolean;
    noiseSuppression: boolean;
    latency: { ideal: number };
} | null {
    if (typeof audio !== "string") return null;
    const parts = audio.split(",");
    if (parts.length < 4) return null;
    const [echoCancellation, autoGainControl, noiseSuppression, ideal] = parts;
    const idealValue = parseFloat(ideal);
    return {
        echoCancellation: echoCancellation.trim() === "1",
        autoGainControl: autoGainControl.trim() === "1",
        noiseSuppression: noiseSuppression.trim() === "1",
        latency: {ideal: Number.isNaN(idealValue) ? 0 : idealValue}
    };
}

function getTurnUrlList(iceServers: unknown): string[] {
    if (!Array.isArray(iceServers)) return [];
    return iceServers.flatMap((server: any) =>
        Array.isArray(server?.urls) ? server.urls : (server?.urls ? [server.urls] : [])
    );
}

function formatTurnUrls(iceServers: unknown): string {
    const urls = getTurnUrlList(iceServers);
    return urls.length ? urls.join(', ') : 'none';
}

function summarizeNewMessageTurn(event: any, message: any) {
    const fromUri = event?.request?.from?.uri?.toString?.() || event?.request?.from || null;
    const data2 = Array.isArray(message?.data2) ? message.data2 : null;

    if (data2?.length) {
        return {
            source: 'newMessage',
            cmd: message?.cmd ?? null,
            originator: event?.originator ?? null,
            from: fromUri,
            iceServers: data2
                .filter((item: any) => item?.uri)
                .map((item: any) => ({
                    urls: item.uri,
                    username: item?.user ?? null,
                    credential: '[userToken]'
                })),
            audio: data2.find((item: any) => item?.audio)?.audio ?? null
        };
    }

    if (message?.data) {
        const protocol = message.data.secure ? 'turns' : 'turn';
        return {
            source: 'newMessage',
            cmd: message?.cmd ?? null,
            originator: event?.originator ?? null,
            from: fromUri,
            iceServers: [{
                urls: `${protocol}:${message.data.ip}:${message.data.port}?transport=${message.data.transport}`,
                username: message.data.user ?? null,
                credential: '[userToken]',
                ip: message.data.ip ?? null,
                port: message.data.port ?? null,
                transport: message.data.transport ?? null,
                secure: !!message.data.secure
            }],
            audio: message.data.audio ?? null
        };
    }

    return {
        source: 'newMessage',
        cmd: message?.cmd ?? null,
        originator: event?.originator ?? null,
        from: fromUri,
        invalid: true
    };
}

function extractHostFromIceUrl(url: string): string {
    const withoutScheme = url.replace(/^(turns?|stuns?):/i, '');
    return withoutScheme.split(/[:?]/)[0]?.toLowerCase() ?? '';
}

function getConfiguredIceUrlKeys(turnUrls?: string, iceServers?: unknown): string[] {
    const urls = iceServers ? getTurnUrlList(iceServers) : [];
    if (urls.length) {
        return urls.map((url) => String(url).trim().toLowerCase());
    }
    if (!turnUrls || turnUrls === 'none') {
        return [];
    }
    return turnUrls
        .split(',')
        .map((url) => url.trim().toLowerCase())
        .filter(Boolean);
}

function uniqueIceHosts(urls: string[]): Set<string> {
    const hosts = new Set<string>();
    urls.forEach((url) => {
        const host = extractHostFromIceUrl(url);
        if (host) {
            hosts.add(host);
        }
    });
    return hosts;
}

function rememberSuccessfulRelayUrl(connection: any, relayUrl: unknown): void {
    if (!connection || relayUrl == null || relayUrl === '') {
        return;
    }
    if (!connection.__successfulRelayUrls) {
        connection.__successfulRelayUrls = new Set<string>();
    }
    connection.__successfulRelayUrls.add(String(relayUrl).trim().toLowerCase());
}

function inferMissingTurnHosts(
    configuredKeys: string[],
    successfulRelayUrls: Set<string>,
    relayCandidateCount: number,
    brokenByUri: Map<string, { url?: string; errorCode?: number | string; errorText?: string; address?: string | null; port?: number | null }>
) {
    if (configuredKeys.length <= 1 || relayCandidateCount === 0) {
        return;
    }

    const attributedHosts = new Set<string>();
    for (const configuredUrl of configuredKeys) {
        const configuredHost = extractHostFromIceUrl(configuredUrl);
        const hasRelayFromHost = Array.from(successfulRelayUrls).some(
            (relayUrl) => extractHostFromIceUrl(relayUrl) === configuredHost
        );
        if (hasRelayFromHost) {
            attributedHosts.add(configuredHost);
        }
    }
    if (attributedHosts.size === 0) {
        return;
    }

    for (const configuredUrl of configuredKeys) {
        const configuredHost = extractHostFromIceUrl(configuredUrl);
        const hasRelayFromHost = attributedHosts.has(configuredHost);
        const alreadyTracked = Array.from(brokenByUri.keys()).some(
            (key) => extractHostFromIceUrl(key) === configuredHost
        );
        if (!hasRelayFromHost && !alreadyTracked) {
            brokenByUri.set(configuredUrl, {
                url: configuredUrl,
                errorCode: 'inferred',
                errorText: 'No relay candidate received from this TURN host during ICE gathering',
                address: null,
                port: null,
            });
            console.warn('[ICE candidate error inferred]', {
                url: configuredUrl,
                configuredHost,
                successfulRelayUrls: Array.from(successfulRelayUrls),
            });
        }
    }
}

function hasUsableTurnCredentials(iceServers: unknown): boolean {
    if (!Array.isArray(iceServers) || iceServers.length === 0) return false;
    return iceServers.some((server: any) => {
        const urls = server?.urls;
        if (Array.isArray(urls)) {
            return urls.some((url) => typeof url === 'string' && url.length > 0);
        }
        return typeof urls === 'string' && urls.length > 0;
    });
}

function getTurnCredentialsUserKey(userName?: string | null, hash?: string | null): string {
    return `${userName ?? ''}:${hash ?? ''}`;
}

function isDocumentHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function requestSipReregister(): void {
    const currentUa = getGlobalJsSIP();
    if (!currentUa) {
        return;
    }
    try {
        if (typeof currentUa.start === 'function') {
            currentUa.start();
        }
        if (typeof currentUa.register === 'function') {
            currentUa.register();
        }
    } catch {
    }
}

const SIPCallComponent = (props: any) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intervalIdRef = useRef<any>(null);
    const jssipTabViewRef = useRef<any>(null);
    const sipInitializedRef = useRef<boolean>(false);
    const lastActiveTabRef = useRef<boolean>(true);
    const sipOwnerUnmountedRef = useRef(false);
    const sipIntentRef = useRef({
        activeTab: true,
        webrtcStartAllowed: false,
        jwt: null as string | null,
        userName: null as string | null,
        hash: null as string | null,
    });
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const turnWaitVisibilityHandlerRef = useRef<(() => void) | null>(null);
    const turnCredentialsUserKeyRef = useRef<string | null>(null);
    const turnProbeSignatureRef = useRef<string | null>(null);
    const iceCandidateErrorConnectionsRef = useRef<WeakSet<any>>(new WeakSet());
    const originalOnFocusRef = useRef<((this: GlobalEventHandlers, ev: FocusEvent) => any) | null>(null);
    const onlineTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const callRef = useRef<any>(null);
    const consultationCallRef = useRef<any>(null);
    const incomingSessionRef = useRef<any>(null);
    const pendingIncomingSessionRef = useRef<any>(null);
    const pendingUserDataCallDetailsRef = useRef<any>(null);
    const groupIdForConsultationRef = useRef<string | null>(null);
    const groupFlowUuidRef = useRef<string | null>(null);
    const recallNumberSessionIdRef = useRef<string | null>(null);
    const flowUuidRef = useRef<string>('');
    const isAcceptingCallRef = useRef<boolean>(false);
    const referSessionRef = useRef<any>(null);
    const referHangupActiveRef = useRef<boolean>(false);
    const referIncludesConsultationRef = useRef<boolean>(false);
    const internetConnection = (navigator as any).connection as NetworkInformation | undefined;
    const isOnline = useEffectiveOnlineStatus();
    const user = useAppSelector(state => state.UsersReducer.userData);
    // @ts-ignore
    const checkUserForCalling = useAppSelector(state => state.UsersListReducer.checkUserForCalling);
    const searchResults = useAppSelector(state => state.WebRTCReducer.searchResults);
    const webRTCError = useAppSelector(state => state.WebRTCReducer.error);
    const [status, setStatus] = useState<string>('Idle');
    const [call, setCall] = useState<any>(null);
    const [consultationCall, setConsultationCall] = useState<any>(false);
    const [isSpyStatus, setisSpyStatus] = useState<any>(false);
    const [newCallInfo, setNewCallInfo] = useState<any>(null);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [queueCallId, setQueueCallId] = useState<number | null>(null);
    const [isConsultationCall, setIsConsultationCall] = useState<boolean | any>(null);
    const [referCall, setReferCall] = useState<boolean>(false);
    const [autocompleteChange, setAutocompleteChange] = useState<boolean>(false);
    const [ua, setUA] = useState<JsSIP.UA | null>(null);
    const [showKeyboard, setShowKeyboard] = useState<boolean>(false);
    const errorJsSip = useAppSelector(state => state.WebRTCReducer.errorJsSip);
    const [closeCallButton, setCloseCallButton] = useState<boolean>(false);
    const toast = useRef<any>(null);
    const keyboardRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
    const [showDialog, setShowDialog] = useState(false);
    const [incomingSession, setIncomingSession] = useState<any>(null);
    const [pendingIncomingSession, setPendingIncomingSession] = useState<any>(null);
    const [volume, setVolume] = useState(0.5);
    const [ringtoneVolume, setRingtoneVolume] = useState(0.25);
    const ringtoneVolumeRef = useRef(0.25);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const holdMusicAudioRef = useRef<HTMLAudioElement | null>(null);
    const [isCallHold, setIsCallHold] = useState<boolean>(false);
    const [isCallMute, setIsCallMute] = useState<boolean>(false);
    const [isConsultationCallHold, setIsConsultationCallHold] = useState<boolean>(false);
    const [isConsultationCallMute, setIsConsultationCallMute] = useState<boolean>(false);
    const [lastCallNumber, setLastCallNumber] = useState<string | null>(null);
    const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
    const endCallAudioRef = useRef<HTMLAudioElement | null>(null);
    const startCallAudioRef = useRef<HTMLAudioElement | null>(null);
    const [callDuration, setCallDuration] = useState<number>(0);
    const [spyDuration, setSpyDuration] = useState<number>(0);
    const [callHoldDuration, setCallHoldDuration] = useState<number>(0);
    const [callMuteDuration, setCallMuteDuration] = useState<number>(0);
    const [consultationCallDuration, setConsultationCallDuration] = useState<number>(0);
    const [consultationCallHoldDuration, setConsultationCallHoldDuration] = useState<number>(0);
    const [consultationCallMuteDuration, setConsultationCallMuteDuration] = useState<number>(0);
    const [incomingCall, setIncomingCall] = useState<boolean>(false);
    const [callDirection, setCallDirection] = useState<any>('');
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [newValue, setNewValue] = useState('');
    const [flowUuid, setFlowUuid] = useState('');
    const muteId = useAppSelector(state => state.WebRTCReducer.muteId);
    const muteConsultationId = useAppSelector(state => state.WebRTCReducer.muteConsultationId);
    const activeSession = useAppSelector(state => state.WebRTCReducer.activeTab);
    const recallNumber = useAppSelector(state => state.WebRTCReducer.recallNumber);
    const recallNumberSessionId = useAppSelector(state => state.WebRTCReducer.sessionId);
    const spySessionId = useAppSelector(state => state.WebRTCReducer.spySessionId);
    const recallNumberChanged = useAppSelector(state => state.WebRTCReducer.recallNumberChanged);
    const pickupCallTrigger = useAppSelector(state => state.WebRTCReducer.pickupCallTrigger);
    const originalTitle = document.title;
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const [visibleConsultation, setVisibleConsultation] = useState(false);
    const [visible, setVisible] = useState(false);
    const [spyCallMode, setSpyCallMode] = useState<"listen" | "talk">("listen");
    const activeCallType =
        call?._request?.headers?.['X-Calltype']?.[0]?.raw ??
        incomingSession?._request?.headers?.['X-Calltype']?.[0]?.raw;
    const showMuteCallControls = activeCallType !== 'spycall' || spyCallMode !== 'listen';
    const monitoringSipData = useAppSelector(state => state.MonitoringListReducer?.monitoringSipData);
    const monitoringSipDataRef = useRef(monitoringSipData);
    const consultationSipData = useAppSelector(state => state.MonitoringListReducer?.consultationSipData);

    const spyStatusSessionId = useMemo(() => {
        const raw = isSpyStatus?.sessionId ?? isSpyStatus?.SessionId;
        return raw != null && raw !== '' ? String(raw) : '';
    }, [isSpyStatus]);

    const monitoringSessionKey = useMemo(() => {
        const raw = monitoringSipData?.sessionId ?? getGroupSessionId(monitoringSipData);
        return raw != null && raw !== '' ? String(raw) : '';
    }, [monitoringSipData]);

    const consultationSessionKey = useMemo(() => {
        const raw = consultationSipData?.sessionId ?? getGroupSessionId(consultationSipData);
        return raw != null && raw !== '' ? String(raw) : '';
    }, [consultationSipData]);

    const spySessionMatchesMonitoring = !!(
        spyStatusSessionId &&
        monitoringSessionKey &&
        spyStatusSessionId === monitoringSessionKey
    );
    const spySessionMatchesConsultation = !!(
        spyStatusSessionId &&
        consultationSessionKey &&
        spyStatusSessionId === consultationSessionKey
    );

    const spySignalOn = !!(isSpyStatus?.isSpyStatus || isSpyStatus?.IsSpyStatus);
    const spyUiActive =
        spySignalOn && (spySessionMatchesMonitoring || spySessionMatchesConsultation);
    const reconnectSession = useAppSelector(state => state.WebRTCReducer.reconnectSession);
    const restartSipConnection = useAppSelector(state => state.WebRTCReducer.checkSipConnection);
    const [userDataHubConnection, setUserDataHubConnection] = useState(null) as any;
    const [userDataCallDetails, setUserDataCallDetails] = useState<any>(null);
    const [pendingUserDataCallDetails, setPendingUserDataCallDetails] = useState<any>(null);
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [selectedConsultationData, setSelectedConsultationData] = useState<any | null>(null);
    const [queueId, setQueueId] = useState<any | null>(null);
    const toastError = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [consultationReferCall, setConsultationReferCall] = useState(false);
    const activeTab = useAppSelector(state => state.WebRTCReducer.activeTab);
    const filesHistory = useAppSelector(state => state.StatisticsListReducer?.filesHistory);
    const autoCompleteRef = useRef<AutoComplete>(null);
    // @ts-ignore
    const [callForExternalNumber, setCallForExternalNumber] = useState<any | null>(user?.callForExternalNumber);
    const endCallAudioPlayingRef = useRef(false);
    const userDataHubError = useAppSelector(state => state.WebRTCReducer.userDataHubError);
    const statusHubError = useAppSelector(state => state.WebRTCReducer.statusHubError);
    const [userDataHubConnected, setUserDataHubConnected] = useState(false);
    const [webrtcStartAllowed, setWebrtcStartAllowed] = useState(false);
    const userAgent = navigator.userAgent;
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    const device = isMobile ? "Mobile" : "Desktop";

    useEffect(() => {
        recallNumberSessionIdRef.current =
            recallNumberSessionId != null && recallNumberSessionId !== ''
                ? String(recallNumberSessionId)
                : null;
    }, [recallNumberSessionId]);

    useEffect(() => {
        flowUuidRef.current = flowUuid;
    }, [flowUuid]);

    useEffect(() => {
        monitoringSipDataRef.current = monitoringSipData;
        const activeCall = callRef.current;
        if (!activeCall) {
            return;
        }
        const groupSessionId = getGroupSessionId(monitoringSipData);
        if (!groupSessionId) {
            return;
        }
        const monitoringDetailId = getCallDetailIdFromData(monitoringSipData);
        const activeDetailId = getCallDetailIdFromSession(activeCall);
        if (
            activeDetailId != null &&
            monitoringDetailId != null &&
            String(monitoringDetailId) !== String(activeDetailId)
        ) {
            return;
        }
        updateGroupIdForConsultation(groupSessionId);
        updateGroupFlowUuid(groupSessionId);
    }, [monitoringSipData]);

    useEffect(() => {
        const hasError = !activeTab;
        if (lastDispatchedErrorJsSipTabRef.current === hasError) {
            return;
        }
        lastDispatchedErrorJsSipTabRef.current = hasError;
        dispatch(setErrorJsSipTab(hasError));
    }, [activeTab, dispatch]);

    const clearGroupIdForConsultation = () => {
        groupIdForConsultationRef.current = null;
    };

    const updateGroupIdForConsultation = (groupSessionId: unknown) => {
        groupIdForConsultationRef.current =
            groupSessionId != null && groupSessionId !== '' ? String(groupSessionId) : null;
    };

    const getGroupIdForConsultation = () =>
        groupIdForConsultationRef.current ?? getGroupSessionId(monitoringSipDataRef.current);

    const clearGroupFlowUuid = () => {
        groupFlowUuidRef.current = null;
    };

    const updateGroupFlowUuid = (groupSessionId: unknown) => {
        groupFlowUuidRef.current =
            groupSessionId != null && groupSessionId !== '' ? String(groupSessionId) : null;
    };

    const getGroupFlowUuid = () =>
        groupFlowUuidRef.current ?? getGroupSessionId(monitoringSipDataRef.current);

    const applyGroupSessionFromDetails = (details: any) => {
        const groupSessionId = getGroupSessionId(details);
        updateGroupIdForConsultation(groupSessionId);
        updateGroupFlowUuid(groupSessionId);
    };

    const getHangupHeader = (hangupType: 'user' | 'refer' | 'refresh' | 'logout' | 'closed'): string => {
        const hangupTypeMap: Record<'user' | 'refer' | 'refresh' | 'logout' | 'closed', string> = {
            'user': 'by_user_btnclick',
            'refer': 'by_user_referclick',
            'refresh': 'by_page_unload',
            'logout': 'by_user_logout',
            'closed': 'by_page_unload'
        };
        return `X-hangup: ${hangupTypeMap[hangupType]}`;
    };

    const getSessionDirection = (session: any) =>
        session?._request?.headers?.['X-Direction']?.[0]?.raw ?? '';

    const getActiveReferSession = () =>
        callRef.current || incomingSessionRef.current;

    const markReferHangupActive = (session: any, includeConsultation: boolean = false) => {
        referSessionRef.current = session;
        referHangupActiveRef.current = true;
        referIncludesConsultationRef.current = includeConsultation;
    };

    const clearReferHangupState = () => {
        referSessionRef.current = null;
        referHangupActiveRef.current = false;
        referIncludesConsultationRef.current = false;
    };

    const isReferRelatedSession = (session: any) => {
        if (!referHangupActiveRef.current || !session) {
            return false;
        }

        if (referSessionRef.current === session) {
            return true;
        }

        if (!referIncludesConsultationRef.current) {
            return false;
        }

        return (
            session === callRef.current ||
            session === incomingSessionRef.current ||
            session === consultationCallRef.current
        );
    };

    const resolveHangupHeader = (
        session: any,
        hangupType: 'user' | 'refer' | 'refresh' | 'logout' | 'closed' = 'user'
    ) => {
        if (hangupType === 'refer' || isReferRelatedSession(session)) {
            return getHangupHeader('refer');
        }
        return getHangupHeader(hangupType);
    };

    const hasActiveOrRingingCallContext = () =>
        !!(callRef.current || consultationCallRef.current || incomingSessionRef.current);

    const stopIncomingRingtone = () => {
        if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
        }
    };

    const playIncomingRingtone = () => {
        const audio = ringtoneAudioRef.current;
        if (!audio) {
            return;
        }
        audio.volume = ringtoneVolumeRef.current;
        audio.play().catch(() => {
            setConfirmDialogVisible(true);
        });
    };

    useEffect(() => {
        ringtoneVolumeRef.current = ringtoneVolume;
        if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.volume = ringtoneVolume;
        }
        if (endCallAudioRef.current) {
            endCallAudioRef.current.volume = ringtoneVolume;
        }
    }, [ringtoneVolume]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
        if (holdMusicAudioRef.current) {
            holdMusicAudioRef.current.volume = volume;
        }
    }, [volume]);

    const JS_SIP_STATUS_TERMINATED = 8;
    const JS_SIP_STATUS_CONFIRMED = 9;

    const hasAnotherActiveSession = (sessionToAccept: any) =>
        !!(
            (consultationCallRef.current && consultationCallRef.current !== sessionToAccept) ||
            (callRef.current && callRef.current !== sessionToAccept) ||
            (incomingSessionRef.current && incomingSessionRef.current !== sessionToAccept) ||
            (pendingIncomingSessionRef.current && pendingIncomingSessionRef.current !== sessionToAccept)
        );

    const safeTerminateSession = (
        session: any,
        hangupHeader?: string,
        hangupType: 'user' | 'refer' | 'refresh' | 'logout' | 'closed' = 'user'
    ) => {
        if (!session) {
            return;
        }
        if (session.status === JS_SIP_STATUS_TERMINATED) {
            return;
        }
        if (typeof session.isEnded === 'function' && session.isEnded()) {
            return;
        }
        const resolvedHangupHeader = hangupHeader ?? resolveHangupHeader(session, hangupType);
        try {
            attachHangupHeaderToEarlyCancel(session, resolvedHangupHeader);
            session.terminate({extraHeaders: [resolvedHangupHeader]});
        } catch (error) {
            console.error('Error terminating session:', error);
        }
    };

    const sendReferHangupBye = (session: any) => {
        if (!session) {
            return;
        }
        if (session.status === JS_SIP_STATUS_TERMINATED) {
            return;
        }
        if (typeof session.isEnded === 'function' && session.isEnded()) {
            return;
        }

        const hangupHeader = getHangupHeader('refer');
        try {
            attachHangupHeaderToEarlyCancel(session, hangupHeader);
            session.terminate({extraHeaders: [hangupHeader]});
            return;
        } catch (error) {
            console.error('Error terminating session for refer hangup:', error);
        }

        try {
            if (typeof session.sendRequest === 'function' && session.status !== JS_SIP_STATUS_TERMINATED) {
                session.sendRequest('BYE', {extraHeaders: [hangupHeader]});
            }
        } catch (error) {
            console.error('Error sending refer hangup BYE:', error);
        }
    };

    const clearPendingIncomingState = () => {
        setPendingIncomingSession(null);
        setPendingUserDataCallDetails(null);
        pendingUserDataCallDetailsRef.current = null;
        if (!incomingSessionRef.current && !callRef.current) {
            setIncomingCall(false);
            document.title = originalTitle;
        }
        if (!incomingSessionRef.current && !callRef.current && !consultationCallRef.current) {
            setShowDialog(false);
        }
        stopIncomingRingtone();
    };

    const hasPendingIncomingCall = () => !!pendingIncomingSessionRef.current;

    const cleanupAfterAllCallsEnded = (endedSession?: any) => {
        const endedType = endedSession?._request?.headers?.['X-Calltype']?.[0]?.raw;
        if (endedType === 'spycall') {
            dispatch(setSpySessionId(null));
            setSpyCallMode("listen");
        }
        setIncomingCall(false);
        setIncomingSession(null);
        incomingSessionRef.current = null;
        setCall(null);
        setStatus('Call Ended');
        setIsCallHold(false);
        setIsCallMute(false);
        setVisible(false);
        toggleHoldMusic(false);
        closeDetails();
        clearGroupIdForConsultation();
        clearGroupFlowUuid();
        setQueueId(null);
        setQueueCallId(null);
        setCallDuration(0);
        setSpyDuration(0);
        setCallHoldDuration(0);
        setCallMuteDuration(0);
        stopIncomingRingtone();
        document.title = originalTitle;
        if (!consultationCallRef.current) {
            trackStop();
        }
        if (consultationCallRef.current) {
            setActiveIndex(1);
        }
        playEndCallAudio();
    };

    const cleanupAfterActiveCallEndedWithPendingIncoming = () => {
        setCall(null);
        setPhoneNumber('');
        setIsCallHold(false);
        setIsCallMute(false);
        setVisible(false);
        toggleHoldMusic(false);
        setCallDuration(0);
        setCallHoldDuration(0);
        setCallMuteDuration(0);
        setQueueCallId(null);
        dispatch({type: GET_MONITORING_FOR_SIP, payload: null});
        clearGroupIdForConsultation();
        clearGroupFlowUuid();
        setStatus('Call Ended');
    };

    const logSipFailed = (session: any, extra?: Record<string, unknown>) => {
        logCall('failed', {
            direction: session?.direction,
            callId: getCallIdForCallEvent(flowUuidRef.current, session),
            flowUuid: getSessionFlowUuid(session, flowUuidRef.current),
            cause: extra?.cause,
            callType: session?._request?.headers?.['X-Calltype']?.[0]?.raw,
            ...extra
        });
    };

    const handleCallSessionEnded = (endedSession: any) => {
        logCall('ended', {
            direction: endedSession?.direction,
            callId: getCallIdForCallEvent(flowUuidRef.current, endedSession),
            flowUuid: getSessionFlowUuid(endedSession, flowUuidRef.current)
        });
        if (referHangupActiveRef.current && isReferRelatedSession(endedSession)) {
            safeTerminateSession(endedSession, undefined, 'refer');
            clearReferHangupState();
        }

        if (pendingIncomingSessionRef.current && endedSession === pendingIncomingSessionRef.current) {
            const endedType = endedSession?._request?.headers?.['X-Calltype']?.[0]?.raw;
            if (endedType === 'spycall') {
                dispatch(setSpySessionId(null));
                setSpyCallMode("listen");
            }
            clearPendingIncomingState();
            setStatus('Call Ended');
            if (!callRef.current && !incomingSessionRef.current) {
                playEndCallAudio();
            }
            return;
        }

        if (hasPendingIncomingCall()) {
            cleanupAfterActiveCallEndedWithPendingIncoming();
            return;
        }

        cleanupAfterAllCallsEnded(endedSession);
    };

    const terminatePreviousCallsBeforeAccept = (sessionToExclude?: any) => {
        if (!sessionToExclude || !hasAnotherActiveSession(sessionToExclude)) {
            return;
        }

        const hangupHeader = getHangupHeader('user');

        if (consultationCallRef.current && consultationCallRef.current !== sessionToExclude) {
            safeTerminateSession(consultationCallRef.current, hangupHeader);
            setConsultationCall(false);
        }
        if (callRef.current && callRef.current !== sessionToExclude) {
            safeTerminateSession(callRef.current, hangupHeader);
            setCall(null);
            closeDetails();
            setIsCallHold(false);
            setIsCallMute(false);
            setVisible(false);
            toggleHoldMusic(false);
            setQueueId(null);
            setQueueCallId(null);
        }
        if (incomingSessionRef.current && incomingSessionRef.current !== sessionToExclude) {
            safeTerminateSession(incomingSessionRef.current, hangupHeader);
            setIncomingSession(null);
            setIncomingCall(false);
        }
    };

    const terminateAllActiveCallsWithUserHeader = (hangupType: 'user' | 'refresh' | 'logout' | 'closed' = 'user') => {
        const hangupHeader = getHangupHeader(hangupType);

        safeTerminateSession(callRef.current, hangupHeader);
        safeTerminateSession(consultationCallRef.current, hangupHeader);
        safeTerminateSession(incomingSessionRef.current, hangupHeader);
        safeTerminateSession(pendingIncomingSessionRef.current, hangupHeader);
    };

    const logout = (reason: string, details?: Record<string, unknown>) => {
        logLogout(reason, details);
        terminateAllActiveCallsWithUserHeader('logout');
        stopGlobalJsSIP();

        localStorage.removeItem('callcenter:user');

        navigate('/login');
        localStorage.clear();
        localStorage.removeItem('persist:callcenter:user');
        localStorage.removeItem('expirationTime');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('expirationTime');
        localStorage.removeItem('authToken');

        sessionStorage.setItem('isLoggedOut', 'true');

        dispatch(clearAllStates());
        dispatch(setRolesVisible(null));
        dispatch({type: ERROR_ACCOUNT_WEB_LOGIN, payload: {"statusCode": null, "title": null, "message": null, "data": null}});
        restoreUserConsoleLogsToStorage();
    }


    const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    const statusHubLogoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    // TEMP disabled for deploy: logoutActionRef / hub error refs for the background-tab fix.
    const lastDispatchedSipUserBusyRef = useRef<boolean | null>(null);
    const lastDispatchedErrorJsSipTabRef = useRef<boolean | null>(null);

    useEffect(() => {
        callRef.current = call;
        setGlobalCall(call);
    }, [call]);

    useEffect(() => {
        consultationCallRef.current = consultationCall;
        setGlobalConsultationCall(consultationCall);
    }, [consultationCall]);

    useEffect(() => {
        incomingSessionRef.current = incomingSession;
        setGlobalIncomingSession(incomingSession);
    }, [incomingSession]);

    useEffect(() => {
        pendingIncomingSessionRef.current = pendingIncomingSession;
        setGlobalPendingIncomingSession(pendingIncomingSession);
    }, [pendingIncomingSession]);

    useEffect(() => {
        pendingUserDataCallDetailsRef.current = pendingUserDataCallDetails;
    }, [pendingUserDataCallDetails]);

    useEffect(() => {
        const busy = !!(call || consultationCall || incomingSession || incomingCall || pendingIncomingSession);
        if (lastDispatchedSipUserBusyRef.current === busy) {
            return;
        }
        lastDispatchedSipUserBusyRef.current = busy;
        dispatch(setSipUserBusy(busy));
    }, [call, consultationCall, incomingSession, incomingCall, pendingIncomingSession, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(setSipUserBusy(false));
        };
    }, [dispatch]);

    useEffect(() => {
        let isRefreshKeyPressed = false;
        let hasCalledTerminate = false;

        const SESSION_ID_KEY = 'jsSipSessionId';
        const REFRESH_FLAG_KEY = 'jsSipIsRefreshing';
        const PAGE_LOAD_TIME_KEY = 'jsSipPageLoadTime';


        const currentSessionId = `${Date.now()}-${Math.random()}`;
        sessionStorage.setItem(SESSION_ID_KEY, currentSessionId);
        sessionStorage.setItem(PAGE_LOAD_TIME_KEY, Date.now().toString());

        const handlePageHide = (e: PageTransitionEvent) => {
            if (e.persisted) {
                sessionStorage.setItem(REFRESH_FLAG_KEY, 'true');
                return;
            }

            if (!hasCalledTerminate && !checkIsRefresh()) {
                terminateAllActiveCallsWithUserHeader('closed');
                sessionStorage.removeItem('pageIsRefreshing');
                sessionStorage.removeItem('lastNavigationWasReload');
                sessionStorage.removeItem(REFRESH_FLAG_KEY);
                sessionStorage.removeItem(SESSION_ID_KEY);
                sessionStorage.removeItem(PAGE_LOAD_TIME_KEY);
                hasCalledTerminate = true;
            }
        };

        const handlePageShow = (e: PageTransitionEvent) => {
            if (!e.persisted) {
                try {
                    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
                    if (navigation && navigation.type === 'reload') {
                        sessionStorage.setItem('lastNavigationWasReload', 'true');
                    } else {
                        sessionStorage.removeItem('lastNavigationWasReload');
                    }
                } catch (e) {
                    try {
                        const nav = (performance as any).navigation;
                        if (nav && nav.type === 1) {
                            sessionStorage.setItem('lastNavigationWasReload', 'true');
                        } else {
                            sessionStorage.removeItem('lastNavigationWasReload');
                        }
                    } catch (e2) {
                    }
                }
                sessionStorage.removeItem('pageIsRefreshing');
                sessionStorage.removeItem(REFRESH_FLAG_KEY);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'F5' || (e.ctrlKey && (e.key === 'r' || e.key === 'R'))) {
                isRefreshKeyPressed = true;
                sessionStorage.setItem('pageIsRefreshing', 'true');
            }
        };

        const checkIsRefresh = () => {
            if (isRefreshKeyPressed) {
                return true;
            }

            if (sessionStorage.getItem('pageIsRefreshing') === 'true') {
                return true;
            }

            if (sessionStorage.getItem(REFRESH_FLAG_KEY) === 'true') {
                return true;
            }

            if (sessionStorage.getItem('lastNavigationWasReload') === 'true') {
                return true;
            }

            try {
                const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
                if (navigation && navigation.type === 'reload') {
                    return true;
                }
            } catch (e) {
                try {
                    const nav = (performance as any).navigation;
                    if (nav && nav.type === 1) { // TYPE_RELOAD = 1
                        return true;
                    }
                } catch (e2) {
                }
            }

            return false;
        };

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasCalledTerminate) {
                return;
            }

            const isRefresh = checkIsRefresh();

            if (isRefresh) {
                terminateAllActiveCallsWithUserHeader('refresh');
                sessionStorage.removeItem('pageIsRefreshing');
                sessionStorage.removeItem('lastNavigationWasReload');
                sessionStorage.removeItem(REFRESH_FLAG_KEY);
                hasCalledTerminate = true;
            } else {
                terminateAllActiveCallsWithUserHeader('closed');
                sessionStorage.removeItem('pageIsRefreshing');
                sessionStorage.removeItem('lastNavigationWasReload');
                sessionStorage.removeItem(REFRESH_FLAG_KEY);
                sessionStorage.removeItem(SESSION_ID_KEY);
                sessionStorage.removeItem(PAGE_LOAD_TIME_KEY);
                hasCalledTerminate = true;
            }
        };

        const originalReload = window.location.reload.bind(window.location);
        try {
            Object.defineProperty(window.location, 'reload', {
                value: function () {
                    sessionStorage.setItem('pageIsRefreshing', 'true');
                    originalReload();
                },
                writable: true,
                configurable: true
            });
        } catch (e) {
        }

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // TEMP disabled for deploy: do not defer hub logout while the tab is hidden.
    useEffect(() => {
        if (userDataHubError) {
            if (!logoutTimerRef.current) {
                logLogoutPending('userdata_hub_error', {timeoutMs: 60000});
                logoutTimerRef.current = setTimeout(() => {
                    logout('userdata_hub_error_timeout', {timeoutMs: 60000});
                }, 60000);
            }
        } else {
            if (logoutTimerRef.current) {
                logLogoutCancelled('userdata_hub_recovered');
                clearTimeout(logoutTimerRef.current);
                logoutTimerRef.current = null;
            }
        }

        return () => {
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
                logoutTimerRef.current = null;
            }
        };
    }, [userDataHubError]);

    useEffect(() => {
        if (statusHubError) {
            if (!statusHubLogoutTimerRef.current) {
                logLogoutPending('status_hub_error', {timeoutMs: 60000});
                statusHubLogoutTimerRef.current = setTimeout(() => {
                    logout('status_hub_error_timeout', {timeoutMs: 60000});
                }, 60000);
            }
        } else {
            if (statusHubLogoutTimerRef.current) {
                logLogoutCancelled('status_hub_recovered');
                clearTimeout(statusHubLogoutTimerRef.current);
                statusHubLogoutTimerRef.current = null;
            }
        }

        return () => {
            if (statusHubLogoutTimerRef.current) {
                clearTimeout(statusHubLogoutTimerRef.current);
                statusHubLogoutTimerRef.current = null;
            }
        };
    }, [statusHubError]);

    let data = {
        "userName": phoneNumber,
        "lastName": "",
        "firstName": "",
        "middleName": "",
        "isLogin": true,
        "mainBranchId": 0,
        isInput: true
    }

    let updatedResults = searchResults;

    const hasActiveCall = !!(call || consultationCall);
    const hasNoBackendResults = !searchResults || searchResults.length === 0;

    const shouldShowManualNumber =
        (!hasActiveCall && !!phoneNumber) ||
        (hasActiveCall && !!phoneNumber && hasNoBackendResults && callForExternalNumber);

    if (shouldShowManualNumber) {
        updatedResults = [data, ...searchResults];
    }
    const userStatus = useAppSelector(state => state.UsersListReducer.userStatus);
    const userStatusObj = Array.isArray(userStatus) && userStatus.length > 0 ? userStatus[0] : userStatus;
    let decodedToken: DecodedToken;
    if (jwtToken?.jwtToken) {
        decodedToken = jwtDecode(jwtToken?.jwtToken);
    }

    const [muteCallData] = useState({
        id: 0,
        flow_uuid: null,
        Sip_auth_username: user?.userName,
        timeStart: null,
        call_id: null
    });

    const [unMuteCallData] = useState({
        id: 0,
        flow_uuid: null,
        Sip_auth_username: user?.userName,
        timeEnd: null,
        call_id: null
    });

    const [microphoneAccess, setMicrophoneAccess] = useState('unknown');

    useEffect(() => {
        let permissionStatus: PermissionStatus;

        const checkMicrophoneAccess = async () => {
            try {
                permissionStatus = await navigator.permissions.query({
                    name: 'microphone' as PermissionName,
                });

                setMicrophoneAccess(permissionStatus.state);

                permissionStatus.onchange = () => {
                    setMicrophoneAccess(permissionStatus.state);
                };
            } catch (error: any) {
                logAction('microphone_permission_check_failed', {message: error?.message || String(error)});
                console.error('Ошибка проверки доступа к микрофону:', error);
            }
        };

        checkMicrophoneAccess();

        return () => {
            if (permissionStatus) {
                permissionStatus.onchange = null;
            }
        };
    }, []);

    useEffect(() => {
        setCall(null);
        setConsultationCall(null);
    }, []);

    useEffect(() => {
        const checkDirection = /^((8|\+7|\+3)[\- ]?)?(\(?\d{3}\)?[\- ]?)?[\d\- ]{7,10}$/;

        if (checkUserForCalling && !referCall && !call) {
            if (queueId) {
                makeCall(queueId);
            } else {
                if (!checkDirection.test(newValue)) {
                    setCallDirection('p2p');
                } else {
                    setCallDirection('outbound');
                }
                makeCall(newValue);
            }
        } else if (checkUserForCalling && !referCall && call) {
            if (queueId) {
                makeConsultationCall(queueId);
            } else {
                call.hold();
                makeConsultationCall(newValue);
            }
            holdCall();
        } else if (checkUserForCalling && referCall && getActiveReferSession()) {
            if (queueId) {
                handleReferCall(queueId, queueId);
            } else {
                if (!checkDirection.test(newValue)) {
                    setCallDirection('p2p');
                } else {
                    setCallDirection('outbound');
                }
                handleReferCall(newValue, queueCallId);
            }
        }
    }, [checkUserForCalling]);

    useEffect(() => {
        if (!call) {
            if (recallNumber) {
                setNewValue(recallNumber);
                setPhoneNumber(recallNumber)
                if (recallNumberSessionId) {
                    setCallDirection('с2с');
                }
                setReferCall(false);
                dispatch(checkUserForCall(recallNumber, jwtToken?.jwtToken));
            }
        }
    }, [recallNumber, recallNumberChanged]);

    useEffect(() => {
        if (pickupCallTrigger) {
            const hangupHeader = getHangupHeader('user');
            if (call) {
                safeTerminateSession(call, hangupHeader);
                setCall(null);
            }

            if (consultationCall) {
                safeTerminateSession(consultationCall, hangupHeader);
                setConsultationCall(false);
            }

            if (incomingSession) {
                safeTerminateSession(incomingSession, hangupHeader);
                setIncomingSession(null);
            }

            if (pendingIncomingSession) {
                safeTerminateSession(pendingIncomingSession, hangupHeader);
                setPendingIncomingSession(null);
            }

            setIncomingCall(false);
            setReferCall(false);
            setConsultationReferCall(false);
            setCloseCallButton(false);
        }
    }, [pickupCallTrigger]);

    useEffect(() => {
        if ((call || incomingCall) && consultationCall) {
            setCloseCallButton(true);
        } else {
            setCloseCallButton(false);
        }
    }, [call, consultationCall, incomingCall]);

    useEffect(() => {
        if (!call && !incomingSession) {
            dispatch(setSpySessionId(null));
            setSpyCallMode("listen");
        }
    }, [call, incomingSession]);

    useEffect(() => {
        const sessionForDialog = pendingIncomingSession || ((!call && !consultationCall) ? incomingSession : null);

        const canAutoAcceptSession =
            sessionForDialog &&
            sessionForDialog.status !== JS_SIP_STATUS_TERMINATED &&
            sessionForDialog.status !== JS_SIP_STATUS_CONFIRMED &&
            !isAcceptingCallRef.current;

        if (canAutoAcceptSession && sessionForDialog?._request?.headers?.['X-Direction']?.[0]?.raw === "c2c" && sessionForDialog?._request?.headers?.['X-Calltype']?.[0]?.raw === "external") {
            props.profileMenuActiveChange(true);
            setConfirmDialogVisible(false);
            stopIncomingRingtone();
            acceptCall();
        } else if (canAutoAcceptSession && sessionForDialog?._request?.headers?.['X-Calltype']?.[0]?.raw === "pickupcall") {
            props.profileMenuActiveChange(true);
            setConfirmDialogVisible(false);
            stopIncomingRingtone();
            acceptCall();
        } else if (canAutoAcceptSession && sessionForDialog?._request?.headers?.['X-Calltype']?.[0]?.raw === "spycall") {
            props.profileMenuActiveChange(true);
            setConfirmDialogVisible(false);
            setSpyCallMode("listen");
            stopIncomingRingtone();
            acceptCall();
        } else if (sessionForDialog) {
            setShowDialog(true);
            props.profileMenuActiveChange(true);
        } else {
            setShowDialog(false);
        }
    }, [incomingSession, pendingIncomingSession, call, consultationCall]);

    const handleToggleSpyCallMode = async () => {
        if (!spySessionId) {
            dispatch({
                type: SET_USER_STATUS_ERROR,
                payload: {message: "Сесія spy не знайдена. Запустіть spy з деталей дзвінка."}
            });
            return;
        }
        const nextSpyMode = spyCallMode === "listen" ? "talk" : "listen";
        const data = {
            userName: user?.userName,
            sessionId: spySessionId,
            type: nextSpyMode
        };
        const isSuccess = await dispatch(checkSessionForSpy(data, jwtToken?.jwtToken) as any);

        if (isSuccess) {
            setSpyCallMode(nextSpyMode);
        }
    };

    useEffect(() => {
        const updateDurations = () => {
            if (call) {
                setCallDuration(prevDuration => prevDuration + 1);
            }

            if (spyUiActive) {
                setSpyDuration(prevDuration => prevDuration + 1);
            }

            if (isCallHold) {
                setCallHoldDuration(prevDuration => prevDuration + 1);
            }

            if (isCallMute) {
                setCallMuteDuration(prevDuration => prevDuration + 1);
            }
        };

        const interval = setInterval(updateDurations, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [call, isCallHold, isCallMute, incomingSession, spyUiActive]);

    useEffect(() => {
        if (!spyUiActive) {
            setSpyDuration(0);
        }
    }, [spyUiActive]);

    useEffect(() => {
        const updateConsultationDurations = () => {
            if (consultationCall) {
                setConsultationCallDuration(prevDuration => prevDuration + 1);
            }

            if (isConsultationCallHold) {
                setConsultationCallHoldDuration(prevDuration => prevDuration + 1);
            }

            if (isConsultationCallMute) {
                setConsultationCallMuteDuration(prevDuration => prevDuration + 1);
            }
        };

        const interval = setInterval(updateConsultationDurations, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [consultationCall, isConsultationCallHold, isConsultationCallMute]);

    JsSIP.debug.disable();

    const [networkInfo, setNetworkInfo] = useState({
        downlink: internetConnection?.downlink ?? null,
        effectiveType: internetConnection?.effectiveType ?? null
    });

    const getSignalStrength = (online: boolean, downlink: number | null, effectiveType: string | null): number => {
        if (!online) return 0;
        if (downlink === null || effectiveType === null) return 1;
        if (downlink < 1 || effectiveType?.includes('2g')) return 1;
        if (downlink < 2.5) return 2;
        return 3;
    };

    useEffect(() => {
        const updateNetworkInfo = () => {
            setNetworkInfo({
                downlink: internetConnection?.downlink ?? null,
                effectiveType: internetConnection?.effectiveType ?? null
            });
        };

        updateNetworkInfo();

        window.addEventListener('online', updateNetworkInfo);
        window.addEventListener('offline', updateNetworkInfo);
        internetConnection?.addEventListener('change', updateNetworkInfo);

        const intervalId = setInterval(() => {
            updateNetworkInfo();
        }, 5000);

        return () => {
            window.removeEventListener('online', updateNetworkInfo);
            window.removeEventListener('offline', updateNetworkInfo);
            internetConnection?.removeEventListener('change', updateNetworkInfo);
            clearInterval(intervalId);
        };
    }, [internetConnection]);

    const signalStrength: any = getSignalStrength(isOnline, networkInfo.downlink, networkInfo.effectiveType);
    let strengthClass = '';

    switch (signalStrength) {
        case 1:
            strengthClass = 'weak';
            break;
        case 2:
            strengthClass = 'medium';
            break;
        case 3:
            strengthClass = 'strong';
            break;
        default:
            strengthClass = 'signal-icon-offline';
            break;
    }

    const updateFiles = (newFile: any) => {
        dispatch(updateFilesHistoryFromHub([newFile, ...filesHistory]));
    }

    const userDataHubStartingRef = useRef(false);
    const reconnectionTimerRef = useRef<NodeJS.Timeout | null>(null);
    const userDataHubConnectionRef = useRef<any>(null);
    const reconnectionAttemptsRef = useRef(0);
    const lastReconnectionTimeRef = useRef(0);
    const currentUserDataHubConnectionRef = useRef<any>(null);


    const isOnLoginPage = () => {
        const pathname = window.location.pathname;
        const hash = window.location.hash;


        if (pathname === '/login') {
            return true;
        }

        const isMainLogin = hash === '#/login' || hash === '#/login/';
        const isAuthGoogle = hash?.includes("auth_google?id");
        const isAuthError = hash?.includes("authError/error?notFound") || hash?.includes("authError/error?exist");
        const isRecoveryPass = hash?.includes("pass/recovery?id");
        const isRecoveryPassEmail = hash?.includes("auth/forgotpassword");

        const isSpecialPage = isMainLogin || isAuthGoogle || isAuthError || isRecoveryPass || isRecoveryPassEmail;

        return isSpecialPage;
    };

    const canConnect = () => {
        const hasToken = sessionStorage.getItem('authToken') || jwtToken?.jwtToken;
        const isLoggedOut = sessionStorage.getItem('isLoggedOut') === 'true';
        const onLoginPage = isOnLoginPage();

        const effectivelyOnLoginPage = onLoginPage || !hasToken;

        return {
            canConnect: hasToken && !isLoggedOut && isOnline && !effectivelyOnLoginPage,
            hasToken: !!hasToken,
            isLoggedOut,
            isOnline,
            onLoginPage: effectivelyOnLoginPage
        };
    };

    const prevReachableRef = useRef(isOnline);

    const clearAllTimers = () => {
        if (reconnectionTimerRef.current) {
            clearTimeout(reconnectionTimerRef.current);
            reconnectionTimerRef.current = null;
        }
        userDataHubStartingRef.current = false;

    };

    const startUserDataHubConnection = (token: string) => {
        const connectionState = canConnect();
        if (!connectionState.canConnect) {
            return null;
        }
        if (connectionState.onLoginPage) {
            return null;
        }

        if (!token) {
            return null;
        }

        if (userDataHubStartingRef.current) {
            return null;
        }
        userDataHubStartingRef.current = true;

        const connection = new HubConnectionBuilder()
            .withUrl(`${WSS_URL}UserDataHub`, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
                accessTokenFactory: () => token
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // TEMP disabled for deploy: nextHubReconnectDelay(retryContext.previousRetryCount)
                    if (retryContext.previousRetryCount < 3) {
                        return 5000;
                    }

                    return 30000;
                }
            })
            .build();

        connection.serverTimeoutInMilliseconds = 60000;
        connection.keepAliveIntervalInMilliseconds = 15000;

        connection.onreconnected(() => {
            logHub('UserDataHub', 'reconnected');
            console.log(`[${new Date().toISOString()}] Information: WebSocket reconnected to ${WSS_URL}UserDataHub`);
            setUserDataHubConnected(true);
            connection.invoke("UserConnectedWithType", decodedToken[`x-access-userid`], "cc").then(() => {
                dispatch(setUserDataHubError(false));
            })
                .catch((error: any) => {
                    logHub('UserDataHub', 'invoke_failed', {message: error?.message, method: 'UserConnectedWithType'});
                    dispatch(setStatusHubError(true));
                }).catch(err => console.error(err));
            dispatch(setUserDataHubError(false));
        });

        connection.onclose(async () => {
            logHub('UserDataHub', 'disconnected');
            console.log(`[${new Date().toISOString()}] Information: WebSocket disconnected. Attempting to reconnect to ${WSS_URL}UserDataHub`);
            dispatch(setUserDataHubError(true));
            setUserDataHubConnected(false);
            setUserDataHubConnection(null);
            if (connection === currentUserDataHubConnectionRef.current) {
                currentUserDataHubConnectionRef.current = null;
            }
            if (connection === userDataHubConnectionRef.current) {
                userDataHubConnectionRef.current = null;
            }
            userDataHubStartingRef.current = false;
        });

        connection.onreconnecting(() => {
            logHub('UserDataHub', 'reconnecting');
            console.warn(`[${new Date().toISOString()}] Warning: WebSocket reconnecting...`);
            dispatch(setUserDataHubError(true));
            setUserDataHubConnected(false);
            userDataHubStartingRef.current = false;
        });

        const attemptStart = () => {
            connection.start()
                .then(() => {
                    logHub('UserDataHub', 'connected');
                    setUserDataHubConnected(true);
                    dispatch(setUserDataHubError(false));
                    connection.off('CallEventInfo');
                    connection.off('File');
                    connection.off('DeletePickupCall');
                    connection.off('SpyStatus');
                    connection.off('Transcribe');
                    connection.on('CallEventInfo', updateStatus => {
                        const details = JSON.parse(updateStatus);
                        if (shouldDeferCallEventDetails(details)) {
                            const normalizedDetails = keysFirstLetterToLowerCase(details);
                            pendingUserDataCallDetailsRef.current = normalizedDetails;
                            setPendingUserDataCallDetails(normalizedDetails);
                            return;
                        }
                        setUserDataCallDetails(details);
                        setCallForExternalNumber(details?.CallForExternalNumber ?? details?.callForExternalNumber);
                    });
                    connection.on('QueueMonitor', monitoringQueue => {
                        let detailsInfo = JSON.parse(monitoringQueue);
                        if (!detailsInfo) return;

                        if (detailsInfo.CountActiveCalls !== undefined && detailsInfo.WithNotification !== undefined) {
                            dispatch(setQueueMonitorStats({
                                countActiveCalls: detailsInfo.CountActiveCalls,
                                withNotification: detailsInfo.WithNotification
                            }));
                        }
                        dispatch(getNewMonitoringQueue(detailsInfo))
                    });
                    connection.on('DeletePickupCall', sessionId => {
                        if (!sessionId) return;
                        dispatch(removeFromMonitoringQueue(sessionId));
                    });

                    connection.on('SpyStatus', spyStatusPayload => {
                        const lowercaseFirstLetterKeys = (obj: Record<string, any> | any): Record<string, any> => {
                            return Object.entries(obj).reduce((acc, [key, value]) => {
                                const newKey = key.charAt(0).toLowerCase() + key.slice(1);
                                acc[newKey] = value;
                                return acc;
                            }, {} as Record<string, any>);
                        };
                        const parsed = lowercaseFirstLetterKeys(JSON.parse(spyStatusPayload));
                        setisSpyStatus(parsed);
                    });

                    connection.on('Transcribe', transcribePayload => {
                        try {
                            const parsed = JSON.parse(transcribePayload);
                            dispatch(setTranscriptionHubStatus({
                                sessionId: parsed.SessionId ?? parsed.sessionId ?? null,
                                callDetailId: parsed.CallDetailId ?? parsed.callDetailId ?? null,
                                status: parsed.Status ?? parsed.status ?? null,
                                tick: Date.now(),
                            }));
                        } catch (error) {
                            console.error('[UserDataHub] Failed to parse Transcribe payload:', error);
                        }
                    });

                    connection.on('File', message => {
                        dispatch(showStatisticsExportLoader(false));
                        let fileMessage = JSON.parse(message);

                        const lowercaseFirstLetterKeys = (obj: Record<string, any>): Record<string, any> => {
                            return Object.entries(obj).reduce((acc, [key, value]) => {
                                const newKey = key.charAt(0).toLowerCase() + key.slice(1);
                                acc[newKey] = value;
                                return acc;
                            }, {} as Record<string, any>);
                        };

                        let newFile = lowercaseFirstLetterKeys(fileMessage);
                        if (newFile?.tempUrl) {
                            dispatch(updateFilesHistoryFromHub(newFile));
                            dispatch({type: SET_UPLOAD_FILE_STATUS_SUCCESS, payload: {message: newFile?.message}});
                        } else if (!newFile?.tempUrl) {
                            if (!newFile.isError) {
                                dispatch({type: SET_USER_STATUS_SUCCESS, payload: {message: newFile?.message}});
                            } else {
                                dispatch({type: SET_USER_STATUS_ERROR, payload: {message: newFile?.message}})
                            }
                        }
                    });
                    connection.invoke("UserConnectedWithType", decodedToken[`x-access-userid`], "cc").then(() => {
                        dispatch(setUserDataHubError(false));
                    })
                        .catch((error: any) => {
                            logHub('UserDataHub', 'invoke_failed', {message: error?.message, method: 'UserConnectedWithType'});
                            dispatch(setUserDataHubError(true));
                        });
                    changeSession();
                    userDataHubStartingRef.current = false;
                })
                .catch((error) => {
                    logHub('UserDataHub', 'failed', {message: error?.message});
                    console.error('[UserDataHub] Connection failed:', error);
                    dispatch(setUserDataHubError(true));
                    setUserDataHubConnected(false);
                    userDataHubStartingRef.current = false;
                });
        };

        attemptStart();

        setUserDataHubConnection(connection);
        currentUserDataHubConnectionRef.current = connection;
        registerHubConnection(connection);

        return connection;
    };

    // TEMP disabled for deploy: do not restart UserDataHub when the tab becomes visible.

    useEffect(() => {
        const connectionState = canConnect();

        if (connectionState.onLoginPage) {
            if (userDataHubConnection) {
                try {
                    const state = userDataHubConnection.state;
                    userDataHubConnection.off('CallEventInfo');
                    userDataHubConnection.off('File');
                    userDataHubConnection.off('SpyStatus');
                    userDataHubConnection.off('Transcribe');
                    if (state === 'Connected' || state === 'Connecting') {
                        userDataHubConnection.stop().catch((error: any) => {
                            if (error.name !== 'AbortError' && error.message?.indexOf('stop() was called') === -1) {
                                console.error('[UserDataHub] Error stopping connection:', error);
                            }
                        });
                    }
                    unregisterHubConnection(userDataHubConnection);
                    setUserDataHubConnection(null);
                    setUserDataHubConnected(false);
                    if (userDataHubConnection === currentUserDataHubConnectionRef.current) {
                        currentUserDataHubConnectionRef.current = null;
                    }
                    userDataHubConnectionRef.current = null;
                    userDataHubStartingRef.current = false;
                } catch (error) {
                    console.error('[UserDataHub] Error stopping connection:', error);
                }
            }

            clearAllTimers();
            return;
        }

        if (!reconnectSession && connectionState.canConnect) {
            const connection = startUserDataHubConnection(jwtToken?.jwtToken || "");
            if (connection) {
                userDataHubConnectionRef.current = connection;
            }

            return () => {
                try {
                    if (connection) {
                        connection?.off?.('CallEventInfo');
                        connection?.off?.('File');
                        connection?.off?.('SpyStatus');
                    }
                } catch {
                }
                if (connection) {
                    const state = connection.state;
                    if (state === 'Connected' || state === 'Connecting') {
                        connection.stop().catch((error: any) => {
                            if (error.name !== 'AbortError' && error.message?.indexOf('stop() was called') === -1) {
                                console.error('[UserDataHub] Error stopping connection:', error);
                            }
                        });
                    }
                    unregisterHubConnection(connection);
                    if (connection === currentUserDataHubConnectionRef.current) {
                        currentUserDataHubConnectionRef.current = null;
                    }
                    userDataHubConnectionRef.current = null;
                }
                clearAllTimers();
            };
        }
        }, [reconnectSession, jwtToken?.jwtToken]);

    useEffect(() => {
        if (!jwtToken?.jwtToken) {
            setWebrtcStartAllowed(false);
            return;
        }

        const isStatusHubConnected = props.statusHubConnectionState === 'Connected';
        if (isStatusHubConnected && userDataHubConnected) {
            setWebrtcStartAllowed((alreadyAllowed) => {
                if (!alreadyAllowed) {
                    console.log(`[${new Date().toISOString()}] Information: StatusHub and UserDataHub connected, starting WebRTC`);
                }
                return true;
            });
        }
    }, [props.statusHubConnectionState, userDataHubConnected, jwtToken?.jwtToken]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            const connectionState = canConnect();
            if (!connectionState.canConnect || connectionState.onLoginPage) {
                return;
            }

            const state = userDataHubConnection?.state;
            const isConnectedOrConnecting = state === 'Connecting' || state === 'Connected';

            dispatch(setUserDataHubError(!(isOnline && isConnectedOrConnecting)));

            if (isOnline && (state === 'Disconnected' || !userDataHubConnection) && !userDataHubConnectionRef.current && !reconnectSession && !userDataHubStartingRef.current) {
                const now = Date.now();
                const timeSinceLastAttempt = now - lastReconnectionTimeRef.current;

                let shouldAttempt = false;

                if (reconnectionAttemptsRef.current < 3) {
                    shouldAttempt = timeSinceLastAttempt >= 5000;
                } else {
                    shouldAttempt = timeSinceLastAttempt >= 30000;
                }

                if (shouldAttempt) {
                    reconnectionAttemptsRef.current++;
                    lastReconnectionTimeRef.current = now;
                    const connection = startUserDataHubConnection(jwtToken?.jwtToken || "");
                    if (connection) {
                        userDataHubConnectionRef.current = connection;
                    }
                }
            }
        }, 5000);

        return () => clearInterval(intervalId);
    }, [userDataHubConnection, isOnline, reconnectSession, jwtToken?.jwtToken]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            const state = userDataHubConnection?.state;
            const isConnectedOrConnecting = state === 'Connecting' || state === 'Connected';

            if (!userDataHubStartingRef.current) {
                dispatch(setUserDataHubError(!(isOnline && isConnectedOrConnecting)));
            }
        }, 5000);

        return () => {
            clearInterval(intervalId);
            clearAllTimers();
        };
    }, [userDataHubConnection, isOnline, jwtToken?.jwtToken]);

    useEffect(() => {
        const getUserDataHubConnection = () => {
            if (userDataHubStartingRef.current) {
                return;
            }

            if (isOnline) {
                dispatch(setUserDataHubError(!(userDataHubConnection?.state === 'Connecting' || userDataHubConnection?.state === 'Connected')));
            } else {
                dispatch(setUserDataHubError(true));
            }
        };

        const intervalId = setInterval(getUserDataHubConnection, 3000);

        return () => {
            clearInterval(intervalId);
            clearAllTimers();
        };
    }, [userDataHubConnection, isOnline]);

    function keysFirstLetterToLowerCase(obj: any): any {
        if (Array?.isArray(obj)) {
            return obj.map(item => keysFirstLetterToLowerCase(item));
        } else if (obj !== null && typeof obj === 'object') {
            return Object.keys(obj).reduce((acc: any, key: string) => {
                const firstLetterLowerCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
                acc[firstLetterLowerCaseKey] = keysFirstLetterToLowerCase(obj[key]);
                return acc;
            }, {});
        }
        return obj;
    }

    const getCallDetailIdFromDetails = (details: any) => {
        const normalized = keysFirstLetterToLowerCase(details);
        return normalized?.callDetailId ?? normalized?.id ?? null;
    };

    const getCallDetailIdFromSession = (session: any) =>
        session?._request?.headers?.['X-Calldetailid']?.[0]?.raw ?? null;

    const isConsultationCallDetails = (details: any) => {
        const normalized = keysFirstLetterToLowerCase(details);
        const callType = normalized?.callType ?? details?.CallType;
        return callType === 'consultationInternal' || callType === 'consultationExternal';
    };

    const isConsultationSession = (session: any) => {
        const callType = session?._request?.headers?.['X-Calltype']?.[0]?.raw;
        return callType === 'consultationInternal' || callType === 'consultationExternal';
    };

    const hasIncomingConsultationContext = () =>
        !!(incomingSession && !pendingIncomingSession) || !!pendingIncomingSession;

    const isSecondaryConsultationContext = () =>
        !!(consultationCallRef.current || consultationCall) && !!(callRef.current || call);

    const applyConsultationDetailsToPrimaryCall = (details: any) => {
        const normalized = keysFirstLetterToLowerCase(details);
        setSelectedRowData(normalized);
        setVisible(true);
        dispatch({type: GET_MONITORING_FOR_SIP, payload: normalized});
        applyGroupSessionFromDetails(normalized);
    };

    const applyConsultationDetailsToSecondaryCall = (details: any) => {
        const normalized = keysFirstLetterToLowerCase(details);
        setSelectedConsultationData(normalized);
        setVisibleConsultation(true);
        dispatch({type: GET_CONSULTATION_FOR_SIP, payload: normalized});
        applyGroupSessionFromDetails(normalized);
    };

    const shouldDeferCallEventDetails = (details: any) => {
        const detailId = getCallDetailIdFromDetails(details);
        if (detailId == null) {
            return false;
        }

        if (isConsultationCallDetails(details)) {
            if (consultationCallRef.current) {
                return false;
            }
            if (callRef.current) {
                const activeDetailId = getCallDetailIdFromSession(callRef.current);
                return activeDetailId != null && String(detailId) !== String(activeDetailId);
            }
            return false;
        }

        if (callRef.current) {
            const activeDetailId = getCallDetailIdFromSession(callRef.current);
            return activeDetailId != null && String(detailId) !== String(activeDetailId);
        }

        if (pendingIncomingSessionRef.current && incomingSessionRef.current) {
            const pendingDetailId = getCallDetailIdFromSession(pendingIncomingSessionRef.current);
            return pendingDetailId != null && String(detailId) === String(pendingDetailId);
        }

        return false;
    };

    const applyPendingCallDetailsToConsultation = () => {
        const details = pendingUserDataCallDetailsRef.current;
        if (!details || !isConsultationCallDetails(details)) {
            return false;
        }
        applyConsultationDetailsToSecondaryCall(details);
        setPendingUserDataCallDetails(null);
        pendingUserDataCallDetailsRef.current = null;
        return true;
    };

    const applyPendingCallDetailsToMonitoring = () => {
        const details = pendingUserDataCallDetailsRef.current;
        if (!details || isConsultationCallDetails(details)) {
            return;
        }
        setSelectedRowData(details);
        setVisible(true);
        dispatch({type: GET_MONITORING_FOR_SIP, payload: details});
        applyGroupSessionFromDetails(details);
        setPendingUserDataCallDetails(null);
        pendingUserDataCallDetailsRef.current = null;
    };

    const applyPendingCallDetails = () => {
        const details = pendingUserDataCallDetailsRef.current;
        if (!details) {
            return;
        }

        if (isConsultationCallDetails(details)) {
            if (isSecondaryConsultationContext()) {
                applyPendingCallDetailsToConsultation();
            } else {
                applyConsultationDetailsToPrimaryCall(details);
                setPendingUserDataCallDetails(null);
                pendingUserDataCallDetailsRef.current = null;
            }
            return;
        }

        applyPendingCallDetailsToMonitoring();
    };

    useEffect(() => {
        if (activeIndex === 0 && (monitoringSipData?.callDetailId || monitoringSipData?.id)) {
            dispatch(getEventMonitorToUserDetail(monitoringSipData?.callDetailId || monitoringSipData?.id, jwtToken?.jwtToken, true));
        } else if (activeIndex === 1 && (consultationSipData?.callDetailId || consultationSipData?.id)) {
            dispatch(getEventMonitorToUserDetail(consultationSipData?.callDetailId || consultationSipData?.id, jwtToken?.jwtToken, true, true));
        }
    }, [activeIndex]);

    useEffect(() => {
        if (!userDataCallDetails) {
            return;
        }

        const details = keysFirstLetterToLowerCase(userDataCallDetails);

        if (isConsultationCallDetails(details)) {
            if (isSecondaryConsultationContext()) {
                applyConsultationDetailsToSecondaryCall(details);
                setUserDataCallDetails(null);
            } else if (call && !consultationCall && !hasIncomingConsultationContext() && !isConsultationSession(call)) {
                pendingUserDataCallDetailsRef.current = details;
                setPendingUserDataCallDetails(details);
                setUserDataCallDetails(null);
            } else if (!consultationCall && (hasIncomingConsultationContext() || isConsultationSession(call))) {
                applyConsultationDetailsToPrimaryCall(details);
                setUserDataCallDetails(null);
            }
            return;
        }

        const canApplyMainMonitoringDetails =
            call ||
            (incomingSession && !pendingIncomingSession && !call) ||
            userStatusObj?.statusesId === 11;

        if (canApplyMainMonitoringDetails && !consultationCall) {
            setSelectedRowData(details);
            setVisible(true);
            dispatch({type: GET_MONITORING_FOR_SIP, payload: details});
            applyGroupSessionFromDetails(details);
            setUserDataCallDetails(null);
        }
    }, [userDataCallDetails, call, incomingSession, pendingIncomingSession, consultationCall, userStatus]);

    useEffect(() => {
        if (consultationCall) {
            applyPendingCallDetailsToConsultation();
        }
    }, [consultationCall]);

    const closeDetailVisible = () => {
        setVisible(false);
        setSelectedRowData(null);
    }

    const checkUser = (value?: any, isRefer?: any, queueId?: number | null) => {
        let newValue = value.replace(/[()\-\s\n\t\r+,;!'"?:$%&/]/g, '');

        if (value) {
            setNewValue(newValue);
            setPhoneNumber(newValue);

            if (queueId) {
                dispatch(checkUserForCall(queueId, jwtToken?.jwtToken));
                setQueueId(queueId);
            } else {
                dispatch(checkUserForCall(newValue, jwtToken?.jwtToken));
                setQueueId(null);
            }

            setReferCall(isRefer);
        } else {
            showError();
        }
    };

    const formatCallDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    };

    const stopAllMicrophones = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === "audioinput");

            if (audioInputs.length === 0) {
                return;
            }

            const audioStream = await navigator.mediaDevices.getUserMedia({audio: true});

            audioStream.getTracks().forEach(track => {
                track.stop();
            });

        } catch (error) {
            console.log(error);
        }
    };

    const trackStop = () => {
        stopAllMicrophones();

        if (audioRef.current) {
            const stream = audioRef.current.srcObject as MediaStream;

            if (stream) {
                stream.getTracks().forEach(track => {
                    track.stop();
                    stream.removeTrack(track);
                });

                audioRef.current.srcObject = null;
            }
        }

        if (call && call.connection) {
            call.connection.getSenders().forEach((sender: any) => {
                if (sender.track) {
                    sender.track.stop();
                    if (call.connection.signalingState !== "closed") {
                        sender.replaceTrack(null);
                        call.connection.removeTrack(sender);
                    }
                }
            });

            call.connection.close();
        }
    }

    const sipCredentialsErrorRef = useRef(false);
    sipIntentRef.current = {
        activeTab: !!activeTab,
        webrtcStartAllowed,
        jwt: jwtToken?.jwtToken ?? null,
        userName: user?.userName ?? null,
        hash: user?.hash ?? null,
    };

    useEffect(() => {
            const sessionJwt = jwtToken?.jwtToken ?? null;
            const sessionUser = user?.userName ?? null;
            const sessionHash = user?.hash ?? null;

            const releaseSip = () => {
                const next = sipIntentRef.current;
                const keepPhone =
                    !sipOwnerUnmountedRef.current &&
                    next.activeTab &&
                    next.webrtcStartAllowed &&
                    !!next.jwt &&
                    !!next.userName &&
                    !!next.hash &&
                    next.jwt === sessionJwt &&
                    next.userName === sessionUser &&
                    next.hash === sessionHash;

                if (keepPhone) {
                    return;
                }

                sipInitializedRef.current = false;
                const handler = turnWaitVisibilityHandlerRef.current;
                if (handler) {
                    document.removeEventListener('visibilitychange', handler);
                    window.removeEventListener('pageshow', handler);
                    document.removeEventListener('resume', handler);
                    turnWaitVisibilityHandlerRef.current = null;
                }
                if (timeoutIdRef.current) {
                    clearTimeout(timeoutIdRef.current);
                    timeoutIdRef.current = null;
                }
                if (originalOnFocusRef.current !== null) {
                    window.onfocus = originalOnFocusRef.current;
                    originalOnFocusRef.current = null;
                } else {
                    window.onfocus = null;
                }
                stopGlobalJsSIP();
                if (!sipOwnerUnmountedRef.current) {
                    setUA(null);
                }
            };

            const missingSipCredentials = !sessionUser || !sessionHash;
            if (!sessionJwt || missingSipCredentials) {
                if (sessionJwt && webrtcStartAllowed && missingSipCredentials && !sipCredentialsErrorRef.current) {
                    sipCredentialsErrorRef.current = true;
                    logCall('sip_credentials_failed', {
                        hasUserName: Boolean(sessionUser),
                        hasHash: Boolean(sessionHash),
                    });
                    dispatch(setErrorJsSip(true));
                }
                return releaseSip;
            }
            sipCredentialsErrorRef.current = false;

            if (!webrtcStartAllowed) {
                return releaseSip;
            }

            const socketUrl = `${WEBRTC_WSS_URL}?access_token=${jwtToken?.jwtToken}`;

            JsSIP.debug.disable();
            JsSIP.debug.log = console.log;
            try {
                const jsSIPConfig = {
                    uri: `sip:${user?.userName}@${WWS_WEBRTC}`,
                    password: `${user?.hash}`,
                    sockets: [new JsSIP.WebSocketInterface(`${socketUrl}`)],
                    session_timers: false,
                    no_answer_timeout: 200,
                    user_agent: Version
                };


                let ua = getGlobalJsSIP();
                const uaState = ua?.state || (ua ? 'Unknown' : null);
                const isJSSIPActive = ua && uaState && (uaState === 'Registered' || uaState === 'Connected' || uaState === 'Started');
                const hasActiveCall = call || incomingSession || incomingCall || pendingIncomingSession;

                const wasInactive = !lastActiveTabRef.current && activeTab;
                const tabBecameActive = wasInactive && activeTab;

                if (activeTab && isJSSIPActive && !reconnectSession && !tabBecameActive) {
                    lastActiveTabRef.current = activeTab;
                    setUA(ua);
                    return releaseSip;
                }

                if (isJSSIPActive && hasActiveCall && !reconnectSession && !tabBecameActive) {
                    lastActiveTabRef.current = activeTab;
                    setUA(ua);
                    return releaseSip;
                }

                if (!activeTab && !reconnectSession) {
                    if (ua) {
                        ua.stop();
                    }
                    stopGlobalJsSIP();
                    setUA(null);
                    lastActiveTabRef.current = activeTab;
                    return releaseSip;
                }

                const shouldReconnect = reconnectSession && !activeSession && !isJSSIPActive;
                const needsReconnect = reconnectSession || (tabBecameActive && !isJSSIPActive);
                const needsInit = !ua || (needsReconnect && !isJSSIPActive) || (shouldReconnect && !isJSSIPActive);
                const needsRestart = wasInactive && ua && uaState && (uaState === 'Disconnected' || uaState === 'Stopped');

                if (activeTab || reconnectSession || tabBecameActive) {
                    if (needsInit) {
                        if (needsReconnect && !isJSSIPActive && ua) {
                            stopGlobalJsSIP();
                            ua = null;
                            sipInitializedRef.current = false;
                        }
                        if (!ua) {
                            initGlobalJsSIP(jsSIPConfig);
                            ua = getGlobalJsSIP();
                            sipInitializedRef.current = true;
                        }
                        if (needsReconnect && !isJSSIPActive) {
                            changeSession();
                            dispatch(setActiveSession(true));
                            dispatch(sessionReconnected(true));
                        }
                    } else if (needsRestart && ua) {
                        ua.start();
                    }

                    lastActiveTabRef.current = activeTab;
                    setUA(ua);

                    if (needsInit && ua && typeof ua.removeAllListeners === 'function') {
                        ua.removeAllListeners();
                    }

                    if (needsInit && ua) {
                        sipInitializedRef.current = true;

                        const clearTurnWaitDeferral = () => {
                            const handler = turnWaitVisibilityHandlerRef.current;
                            if (handler) {
                                document.removeEventListener('visibilitychange', handler);
                                window.removeEventListener('pageshow', handler);
                                document.removeEventListener('resume', handler);
                                turnWaitVisibilityHandlerRef.current = null;
                            }
                        };

                        const clearTurnWaitTimer = () => {
                            if (timeoutIdRef.current) {
                                clearTimeout(timeoutIdRef.current);
                                timeoutIdRef.current = null;
                            }
                            clearTurnWaitDeferral();
                        };

                        const armTurnWaitTimer = () => {
                            clearTurnWaitTimer();
                            timeoutIdRef.current = setTimeout(() => {
                                timeoutIdRef.current = null;
                                if (hasUsableTurnCredentials(callOptions?.pcConfig?.iceServers)) {
                                    return;
                                }
                                if (isDocumentHidden()) {
                                    const onVisible = () => {
                                        if (isDocumentHidden()) {
                                            return;
                                        }
                                        clearTurnWaitDeferral();
                                        if (hasUsableTurnCredentials(callOptions?.pcConfig?.iceServers)) {
                                            return;
                                        }
                                        requestSipReregister();
                                        armTurnWaitTimer();
                                    };
                                    turnWaitVisibilityHandlerRef.current = onVisible;
                                    document.addEventListener('visibilitychange', onVisible);
                                    window.addEventListener('pageshow', onVisible);
                                    document.addEventListener('resume', onVisible);
                                    return;
                                }
                                const errorCode = "webrtc_turn_timeout";
                                if (shouldReportCriticalError(errorCode)) {
                                    dispatch(
                                        reportCriticalError(
                                            {
                                                code:
                                                    errorCode + "\n" +
                                                    "userId: " + (user?.userId ?? "unknown") + "\n" +
                                                    "userName: " + (user?.userName ?? "unknown") + "\n" +
                                                    "version: CC " + Version + "\n" +
                                                    "dateTime: " + new Date().toISOString() + "\n" +
                                                    "device: " + (device ?? "unknown") + "\n" +
                                                    "userAgent: " + (userAgent ?? "unknown") + "\n" +
                                                    "timezone: " + Intl.DateTimeFormat().resolvedOptions().timeZone + "\n" +
                                                    "visibilityState: " + (typeof document !== "undefined" ? document.visibilityState : "unknown") + "\n" +
                                                    "connectionType: " + (internetConnection?.effectiveType ?? "unknown") + "\n" +
                                                    "downlink: " + (internetConnection?.downlink ?? "unknown") + "\n" +
                                                    "url: " + (typeof window !== "undefined" ? window.location.href : "unknown") + "\n" +
                                                    "message: After JsSIP UA start, 5s elapsed waiting for SIP MESSAGE with TURN (turninfo); timer was not cleared by a valid TURN payload.\n" +
                                                    "reason: No usable TURN credentials arrived in time - PBX/WebSocket delay, missing turninfo, or message never reached the client.\n"
                                            },
                                            jwtToken?.jwtToken
                                        )
                                    );
                                }
                                logCall('turn_wait_failed', {timeoutMs: 5000});
                                dispatch(checkSipConnection(true));
                                dispatch(setErrorJsSip(true));
                                requestSipReregister();
                            }, 5000);
                        };

                        const turnUserKey = getTurnCredentialsUserKey(user?.userName, user?.hash);
                        if (turnCredentialsUserKeyRef.current !== turnUserKey) {
                            callOptions.pcConfig.iceServers = [];
                            turnCredentialsUserKeyRef.current = null;
                        }
                        const hasCachedTurnCredentials =
                            turnCredentialsUserKeyRef.current === turnUserKey &&
                            hasUsableTurnCredentials(callOptions?.pcConfig?.iceServers);

                        ua.on('registered', () => {
                            setStatus('Registered');
                            runTurnProbe(callOptions?.pcConfig?.iceServers);
                        });

                        ua.on('registrationFailed', (event: any) => {
                            logCall('sip_registration_failed', {cause: event?.cause, message: event?.message || event?.cause});
                            dispatch(setErrorJsSip(true));
                        });

                        if (!hasCachedTurnCredentials) {
                            armTurnWaitTimer();
                        } else {
                            dispatch(checkSipConnection(false));
                            dispatch(setErrorJsSip(false));
                        }

                        ua.on('newMessage', (e: any) => {
                            clearTurnWaitTimer();
                            let message: any;
                            try {
                                message = JSON.parse(e?.request?.body ?? "null");
                            } catch {
                                recordPinnedUserConsole('error', ['[newMessage] JSON parse failed', {
                                    bodyPreview: String(e?.request?.body ?? '').slice(0, 240)
                                }]);
                                const errorCode = "webrtc_turn_parse_error";
                                if (shouldReportCriticalError(errorCode)) {
                                    dispatch(
                                        reportCriticalError(
                                            {
                                                code:
                                                    errorCode + "\n" +
                                                    "userId: " + (user?.userId ?? "unknown") + "\n" +
                                                    "userName: " + (user?.userName ?? "unknown") + "\n" +
                                                    "version: CC " + Version + "\n" +
                                                    "bodyPreview: " + String(e?.request?.body ?? "").slice(0, 240).replace(/\s+/g, " ") + "\n" +
                                                    "dateTime: " + new Date().toISOString() + "\n" +
                                                    "device: " + (device ?? "unknown") + "\n" +
                                                    "userAgent: " + (userAgent ?? "unknown") + "\n" +
                                                    "timezone: " + Intl.DateTimeFormat().resolvedOptions().timeZone + "\n" +
                                                    "visibilityState: " + (typeof document !== "undefined" ? document.visibilityState : "unknown") + "\n" +
                                                    "connectionType: " + (internetConnection?.effectiveType ?? "unknown") + "\n" +
                                                    "downlink: " + (internetConnection?.downlink ?? "unknown") + "\n" +
                                                    "url: " + (typeof window !== "undefined" ? window.location.href : "unknown") + "\n" +
                                                    "message: On ua 'newMessage', while parsing SIP MESSAGE body as JSON for TURN (turninfo).\n" +
                                                    "reason: JSON.parse failed - body empty, truncated, or not JSON (invalid payload).\n"
                                            }, jwtToken?.jwtToken
                                        )
                                    );
                                }
                                logCall('turn_parse_failed', {
                                    bodyPreview: String(e?.request?.body ?? '').slice(0, 240)
                                });
                                showTurnDataError();
                                dispatch(setErrorJsSip(true));
                                return;
                            }
                            recordPinnedUserConsole('info', ['[newMessage] TURN for sockets', summarizeNewMessageTurn(e, message)]);
                            const data2 = Array.isArray(message?.data2) ? message.data2 : null;
                            if (data2?.length) {
                                if (message.cmd === 'turninfo') {
                                    callOptions.pcConfig.iceServers = data2
                                        .filter((item: any) => item?.uri)
                                        .map((item: any) => ({
                                            urls: item.uri,
                                            username: item?.user,
                                            credential: item?.pass
                                        }));
                                    turnCredentialsUserKeyRef.current = getTurnCredentialsUserKey(user?.userName, user?.hash);
                                    runTurnProbe(callOptions.pcConfig.iceServers);
                                }
                                const audioFromData2 = parseTurnAudioString(data2.find((item: any) => item?.audio)?.audio);
                                if (audioFromData2) {
                                    callOptions.mediaConstraints.audio = {
                                        ...callOptions.mediaConstraints.audio,
                                        ...audioFromData2
                                    };
                                }

                                dispatch(checkSipConnection(false));
                                clearInterval(intervalIdRef.current);
                                intervalIdRef.current = null;
                                dispatch(setErrorJsSip(false));
                                clearCriticalErrorFlags();
                            } else if (message.data) {
                                const protocol = message?.data?.secure ? "turns" : "turn";
                                const urls = `${protocol}:${message?.data?.ip}:${message?.data?.port}?transport=${message?.data?.transport}`;
                                let iceServer = {
                                    urls: urls,
                                    username: message?.data?.user,
                                    credential: message?.data?.pass
                                };
                                if (message.cmd === 'turninfo') {
                                    callOptions.pcConfig.iceServers = [];
                                    callOptions?.pcConfig?.iceServers.push(iceServer);
                                    turnCredentialsUserKeyRef.current = getTurnCredentialsUserKey(user?.userName, user?.hash);
                                    runTurnProbe(callOptions.pcConfig.iceServers);
                                }
                                if (message.data.audio) {
                                    callOptions.mediaConstraints.audio = {
                                        ...callOptions.mediaConstraints.audio,
                                        ...Object.fromEntries(Object.entries(message.data.audio).map(([key, value]) => [key, value]))
                                    };
                                }

                                dispatch(checkSipConnection(false));
                                clearInterval(intervalIdRef.current);
                                intervalIdRef.current = null;
                                dispatch(setErrorJsSip(false));
                                clearCriticalErrorFlags();
                            } else {
                                const errorCode = "webrtc_turn_data_invalid";
                                if (shouldReportCriticalError(errorCode)) {
                                    dispatch(
                                        reportCriticalError(
                                            {
                                                code:
                                                    errorCode + "\n" +
                                                    "userId: " + (user?.userId ?? "unknown") + "\n" +
                                                    "userName: " + (user?.userName ?? "unknown") + "\n" +
                                                    "version: CC " + Version + "\n" +
                                                    "cmd: " + (message?.cmd ?? "unknown") + "\n" +
                                                    "dateTime: " + new Date().toISOString() + "\n" +
                                                    "device: " + (device ?? "unknown") + "\n" +
                                                    "userAgent: " + (userAgent ?? "unknown") + "\n" +
                                                    "timezone: " + Intl.DateTimeFormat().resolvedOptions().timeZone + "\n" +
                                                    "visibilityState: " + (typeof document !== "undefined" ? document.visibilityState : "unknown") + "\n" +
                                                    "connectionType: " + (internetConnection?.effectiveType ?? "unknown") + "\n" +
                                                    "downlink: " + (internetConnection?.downlink ?? "unknown") + "\n" +
                                                    "url: " + (typeof window !== "undefined" ? window.location.href : "unknown") + "\n" +
                                                    "message: On ua 'newMessage', after JSON parsed, before applying TURN to pcConfig (expected message.data).\n" +
                                                    "reason: message.data is missing or falsy - turninfo JSON without server/ip/port/credentials block.\n"
                                            }, jwtToken?.jwtToken
                                        )
                                    );
                                }
                                logCall('turn_data_failed', {cmd: message?.cmd});
                                showTurnDataError();
                                dispatch(setErrorJsSip(true));
                            }
                        });

                        ua.on('unregistered', () => {
                            logCall('sip_unregistered');
                            setStatus('Unregistered');
                        });

                        ua.on('connected', () => {
                            setStatus('Connected');
                        });

                        ua.on('disconnected', () => {
                            logCall('sip_disconnected');
                            setStatus('Disconnected');
                            dispatch(setErrorJsSip(true));
                        });

                        ua.on('error', (error: any) => {
                            logCall('sip_error', {message: error?.message || String(error)});
                            dispatch(setErrorJsSip(true));
                        });

                        ua.on('newRTCSession', (e: { session: any; }) => {
                            function onNewNotification() {
                                document.title = '🔔 Вхідний дзвінок!';
                            }

                            if (!originalOnFocusRef.current) {
                                originalOnFocusRef.current = window.onfocus;
                            }
                            window.onfocus = function () {
                                document.title = originalTitle;
                            };

                            const newSession = e.session;

                            if (newSession?.connection) {
                                const sessionFlowUuid =
                                    getSessionFlowUuid(newSession, flowUuidRef.current);
                                attachIceErrorTracking(newSession.connection, {
                                    direction: newSession.direction,
                                    turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                                    iceServers: callOptions?.pcConfig?.iceServers,
                                    flowUuid: sessionFlowUuid,
                                });
                            }

                            if (newSession && newSession.direction !== 'incoming') {
                                setNewCallInfo(newSession);
                                setIncomingCall(false);
                                newSession.connection.addEventListener('track', (e: any) => {
                                    if (e.track.kind === "audio" && audioRef.current) {

                                        let stream = audioRef.current.srcObject as MediaStream;
                                        if (!stream) {
                                            stream = new MediaStream();
                                            audioRef.current.srcObject = stream;
                                        }

                                        stream.getAudioTracks().forEach(track => stream.removeTrack(track));
                                        stream.addTrack(e.track);

                                        setTimeout(() => {
                                            if (audioRef.current && newSession) {
                                                audioRef.current.play().catch(err => console.error(err));
                                            }
                                        }, 100);
                                    }
                                });
                            }

                            if (newSession.direction === 'incoming') {
                                logCall('incoming_received', {
                                    callId: getCallIdForCallEvent(flowUuidRef.current, newSession),
                                    flowUuid: getSessionFlowUuid(newSession, flowUuidRef.current),
                                    callType: newSession?._request?.headers?.['X-Calltype']?.[0]?.raw
                                });
                                const isPendingIncoming = hasActiveOrRingingCallContext();

                                setIncomingCall(true);
                                if (ringtoneAudioRef) {
                                    playIncomingRingtone();
                                }
                                onNewNotification();

                                if (isPendingIncoming) {
                                    if (pendingIncomingSessionRef.current) {
                                        safeTerminateSession(
                                            pendingIncomingSessionRef.current,
                                            getHangupHeader('user')
                                        );
                                    }
                                    setPendingIncomingSession(newSession);
                                    setShowDialog(true);

                                    newSession.on('ended', () => {
                                        handleCallSessionEnded(newSession);
                                    });

                                    newSession.on('failed', (event: any) => {
                                        logSipFailed(newSession, {cause: event?.cause, stage: 'pending_incoming'});
                                        const failedType = newSession?._request?.headers?.['X-Calltype']?.[0]?.raw;
                                        if (failedType === 'spycall') {
                                            dispatch(setSpySessionId(null));
                                            setSpyCallMode("listen");
                                        }
                                        clearPendingIncomingState();
                                        setStatus('Call failed');
                                        if (!callRef.current && !incomingSessionRef.current) {
                                            playEndCallAudio();
                                        }
                                    });
                                } else {
                                    setCallDirection(getSessionDirection(newSession));
                                    setIncomingSession(newSession);
                                    setFlowUuid(getSessionFlowUuid(newSession) ?? '');
                                    updateGroupFlowUuid(
                                        newSession?._request?.headers?.['X-Group-Flow-Uuid']?.[0]?.raw
                                    );
                                    setShowDialog(true);

                                    newSession.on('ended', () => {
                                        handleCallSessionEnded(newSession);
                                    });

                                    newSession.on('failed', function (event: any) {
                                        logSipFailed(newSession, {cause: event?.cause, stage: 'incoming'});
                                        const failedType = newSession?._request?.headers?.['X-Calltype']?.[0]?.raw;
                                        if (failedType === 'spycall') {
                                            dispatch(setSpySessionId(null));
                                            setSpyCallMode("listen");
                                        }
                                        if (hasPendingIncomingCall()) {
                                            cleanupAfterActiveCallEndedWithPendingIncoming();
                                            return;
                                        }
                                        setIncomingCall(false);
                                        setIncomingSession(null);
                                        incomingSessionRef.current = null;
                                        setStatus('Call failed');
                                        setCall(null);
                                        closeDetails();
                                        setIsCallHold(false);
                                        setIsCallMute(false);
                                        setVisible(false);
                                        toggleHoldMusic(false);
                                        stopIncomingRingtone();
                                        document.title = originalTitle;
                                        if (consultationCall) {
                                            setActiveIndex(1);
                                        }
                                        if (!callRef.current && !consultationCallRef.current) {
                                            trackStop();
                                        }
                                        playEndCallAudio();
                                    });
                                }
                            } else if (newSession.direction === 'outgoing') {
                                setStatus('Outgoing Call');
                            }
                        });

                        if (needsInit) {
                            ua.start();
                        }
                    }
                }

            } catch (error) {
                logCall('sip_init_failed', {message: (error as any)?.message || String(error)});
                dispatch(setErrorJsSip(true));
            }

            return releaseSip;
        }, [reconnectSession, jwtToken?.jwtToken, user?.userName, user?.hash, activeTab, webrtcStartAllowed]
    );

    useEffect(() => {
        sipOwnerUnmountedRef.current = false;
        return () => {
            sipOwnerUnmountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        if (!restartSipConnection || !activeTab) {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
            return;
        }

        const restartUa = () => {
            if (!activeTab || !sipInitializedRef.current) {
                return;
            }
            if (hasUsableTurnCredentials(callOptions?.pcConfig?.iceServers)) {
                dispatch(checkSipConnection(false));
                dispatch(setErrorJsSip(false));
                return;
            }
            console.log(new Date() + ' Attempt to reconnect to SIP');
            requestSipReregister();
        };

        restartUa();
        if (!intervalIdRef.current) {
            intervalIdRef.current = setInterval(restartUa, 5000);
        }

        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };
    }, [restartSipConnection, activeTab, dispatch]);

    useEffect(() => {
        const recoverTurnAfterWake = () => {
            if (isDocumentHidden() || !activeTab || !sipInitializedRef.current) {
                return;
            }
            if (hasUsableTurnCredentials(callOptions?.pcConfig?.iceServers)) {
                return;
            }
            requestSipReregister();
        };

        document.addEventListener('visibilitychange', recoverTurnAfterWake);
        window.addEventListener('pageshow', recoverTurnAfterWake);
        document.addEventListener('resume', recoverTurnAfterWake);

        return () => {
            document.removeEventListener('visibilitychange', recoverTurnAfterWake);
            window.removeEventListener('pageshow', recoverTurnAfterWake);
            document.removeEventListener('resume', recoverTurnAfterWake);
        };
    }, [activeTab]);

    useEffect(() => {
        const handleDeviceChange = async () => {
            if (!call && !incomingSession && !incomingCall) {
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (audioRef.current && (call || consultationCall)) {
                try {
                    let stream = new MediaStream();
                    audioRef.current.srcObject = stream;

                    const devices = await navigator.mediaDevices.enumerateDevices();

                    const activeInput = devices.find(d => d.kind === "audioinput" && d.deviceId !== "default");

                    const outputDevices = devices.filter(d => d.kind === "audiooutput");
                    const activeOutput = outputDevices.length > 0 ? outputDevices[0] : null;

                    let newAudioTrack: MediaStreamTrack | any | null = null;
                    let newMicTrack: MediaStreamTrack | any | null = null;

                    if (call && call.connection) {
                        let attempts = 0;
                        while (attempts < 5) {
                            const receiver = call.connection.getReceivers().find((r: { track: { kind: string; }; }) => r.track?.kind === "audio");
                            if (receiver && (!newAudioTrack || receiver.track.id !== newAudioTrack.id)) {
                                newAudioTrack = receiver.track;
                                break;
                            }
                            await new Promise(resolve => setTimeout(resolve, 500));
                            attempts++;
                        }

                        if (activeInput) {
                            const newStream = await navigator.mediaDevices.getUserMedia({
                                audio: {deviceId: {exact: activeInput.deviceId}}
                            });

                            call.connection.getSenders().forEach((sender: { track: { kind: string; stop: () => void; }; }) => {
                                if (sender.track?.kind === "audio") {
                                    sender.track.stop();
                                }
                            });

                            newMicTrack = newStream.getAudioTracks()[0];

                            if (newMicTrack) {
                                const sender = call.connection.getSenders().find((s: { track: { kind: string; }; }) => s.track?.kind === "audio");
                                if (sender) {
                                    await sender.replaceTrack(newMicTrack);

                                    let confirmationAttempts = 0;
                                    while (confirmationAttempts < 5) {
                                        const updatedSender = call.connection.getSenders().find((s: { track: { kind: string; }; }) => s.track?.kind === "audio");
                                        if (updatedSender?.track?.id === newMicTrack.id) {
                                            break;
                                        }
                                        await new Promise(resolve => setTimeout(resolve, 500));
                                        confirmationAttempts++;
                                    }
                                } else {
                                    console.log("Не знайдено аудіотрек для оновлення");
                                }
                            }
                        }
                    }

                    if (newAudioTrack) {
                        stream.addTrack(newAudioTrack);
                    }

                    setTimeout(() => {
                        if (audioRef.current && (call || consultationCall)) {
                            audioRef.current.play().catch(err => console.log(err));
                        }
                    }, 700);

                    if ("setSinkId" in audioRef.current) {
                        setTimeout(async () => {
                            if (activeOutput) {
                                try {
                                    await (audioRef.current as any).setSinkId(activeOutput.deviceId);
                                } catch (err) {
                                    console.log(`Помилка установки пристрою виведення: ${err}`);
                                }
                            }
                        }, 1000);
                    }

                } catch (error) {
                    console.log(`Помилка при обробці пристроїв: ${error}`);
                }
            }
        };

        navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
        };
    }, [call, incomingSession, incomingCall, consultationCall]);

    useEffect(() => {
        const wasReachable = prevReachableRef.current;
        prevReachableRef.current = isOnline;

        if (!isOnline && wasReachable) {
            logHub('network', 'offline');
            dispatch(setErrorJsSip(true));
            dispatch(setMonitoringHubError(true));
            dispatch(setStatusHubError(true));
            return;
        }

        if (isOnline && !wasReachable && activeTab) {
            logHub('network', 'recovered');
            if (onlineTimeoutRef.current) {
                clearTimeout(onlineTimeoutRef.current);
            }
            dispatch(flushPendingErrorReports(jwtToken?.jwtToken));
            dispatch(setReconnectSession(true));
            onlineTimeoutRef.current = setTimeout(() => {
                dispatch(setErrorJsSip(false));
                dispatch(setReconnectSession(false));
                onlineTimeoutRef.current = null;
            }, 2000);
        }
    }, [activeTab, dispatch, jwtToken?.jwtToken, isOnline]);

    useEffect(() => {
        return () => {
            if (onlineTimeoutRef.current) {
                clearTimeout(onlineTimeoutRef.current);
                onlineTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (call) {
            const handleCallEnded = () => {
                if (call) {
                    handleCallSessionEnded(call);
                }
            };

            call.on('ended', handleCallEnded);

            return () => {
                if (call && typeof call.off === 'function') {
                    call.off('ended', handleCallEnded);
                }
            };
        }
    }, [call, consultationCall]);

    const makeCall = (value?: any) => {
        if (ua && call === null && phoneNumber.trim() !== '') {
            setActiveIndex(0);
            setIsConsultationCall(false);
            let userPhone = phoneNumber
            if (value) {
                userPhone = value;
            }

            const createUuidv4 = () => {
                return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
                    // @ts-ignore
                    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
                );
            }

            let flowUuId = createUuidv4();
            callOptions.extraHeaders = [];
            clearGroupIdForConsultation();
            clearGroupFlowUuid();

            const recallSessionId = recallNumberSessionIdRef.current;
            if (recallSessionId) {
                // @ts-ignore
                callOptions.extraHeaders.push(`X-group_flow_uuid: ${recallSessionId}`);
                updateGroupFlowUuid(recallSessionId);
                dispatch(clearRecallSession());
                // @ts-ignore
                callOptions.extraHeaders.push(`X-campaign_id: callback::0`);
            }
            // @ts-ignore
            callOptions.extraHeaders.push(`X-flow_uuid: ${flowUuId}`);

            flowUuidRef.current = flowUuId;
            setFlowUuid(flowUuId)

            const destination = `sip:${userPhone}@${WWS_WEBRTC}`;
            // @ts-ignore
            const newCall = ua.call(destination, callOptions);
            attachLocalSdpCandidateFilter(newCall);
            logCall('outgoing_created', {flowUuid: flowUuId, number: userPhone, direction: newCall?.direction});
            setLastCallNumber(userPhone);
            setPhoneNumber('');

            const iceT0 = performance.now();
            const iceDt = () => Math.round(performance.now() - iceT0);
            console.log('[ICE timing] call created @0ms', {flowUuid: flowUuId, turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers)});
            newCall.connection?.addEventListener('iceconnectionstatechange', () => {
                console.log('[ICE timing] iceConnectionState=' + newCall.connection?.iceConnectionState + ' @' + iceDt() + 'ms');
            });
            newCall.connection?.addEventListener('icecandidateerror', (err: any) => {
                console.log('[ICE timing] icecandidateerror @' + iceDt() + 'ms', {errorCode: err?.errorCode, url: err?.url, address: err?.address, errorText: err?.errorText});
            });
            newCall.connection?.addEventListener('icegatheringstatechange', () => {
                console.log('[ICE timing] iceGatheringState=' + newCall.connection?.iceGatheringState + ' @' + iceDt() + 'ms, relayCount=' + outgoingRelayCandidateCount);
            });
            let rawCandCount = 0;
            newCall.connection?.addEventListener('icecandidate', (e: any) => {
                if (!e.candidate) {
                    console.log('[ICE timing] gathering done (null candidate) @' + iceDt() + 'ms, totalCandidates=' + rawCandCount);
                    return;
                }
                rawCandCount++;
                console.log('[ICE timing] candidate #' + rawCandCount + ' @' + iceDt() + 'ms', {type: e.candidate.type, protocol: e.candidate.protocol, address: e.candidate.address, port: e.candidate.port, relatedAddress: e.candidate.relatedAddress, url: (e as any)?.url});
            });

            newCall.on('connecting', () => {
                console.log('[ICE timing] connecting (INVITE sent) @' + iceDt() + 'ms');
                setStatus('Calling...');
            });

            let outgoingCandidateCount = 0;
            let outgoingRelayCandidateCount = 0;
            newCall.on('icecandidate', (e: any) => {
                if (!e?.candidate || e.candidate.type !== 'relay') {
                    return;
                }
                outgoingCandidateCount++;
                outgoingRelayCandidateCount++;
                console.log('[ICE timing] relay candidate #' + outgoingRelayCandidateCount + ' @' + iceDt() + 'ms', {protocol: e.candidate.protocol, address: e.candidate.address, url: e.candidate.url});
                const relayUrl = e.candidate.url;
                rememberSuccessfulRelayUrl(newCall?.connection, relayUrl);
                if (outgoingRelayCandidateCount === 1) {
                    console.log('[ICE timing] e.ready() on first relay @' + iceDt() + 'ms');
                    e.ready();
                }
            });

            const terminateCallByError = (errorMessage: string) => {
                if (newCall) {
                    newCall.terminate();
                }
                setPhoneNumber('');
                setCall(null);
                closeDetails();
                setIsCallHold(false);
                setIsCallMute(false);
                setVisible(false);
                toggleHoldMusic(false);
                if (consultationCall) {
                    setActiveIndex(1);
                }

                toastError?.current?.show({severity: 'error', summary: errorMessage, detail: '', life: 5000});

            };

            let iceTimeout = setTimeout(() => {
                console.log('[ICE timing] iceTimeout fired @' + iceDt() + 'ms', {relayCount: outgoingRelayCandidateCount, iceConnectionState: newCall?.connection?.iceConnectionState, iceGatheringState: newCall?.connection?.iceGatheringState});
                if (outgoingRelayCandidateCount > 0) {
                    return;
                }
                console.error('[ICE] call dropped', {
                    userId: user?.userId ?? 'unknown',
                    userName: user?.userName ?? 'unknown',
                    version: 'CC ' + Version,
                    ...(flowUuId ? {flowUuid: flowUuId} : {}),
                    direction: newCall.direction,
                    iceConnectionState: newCall?.connection?.iceConnectionState,
                    turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                    error: outgoingCandidateCount === 0
                        ? '[ICE] call dropped - no candidates gathered in time (broken/unreachable candidates)'
                        : '[ICE] call dropped - ICE gathering did not complete in time',
                });
                logCall('dropped', {
                    reason: 'ice_gathering_timeout',
                    flowUuid: flowUuId,
                    direction: newCall.direction,
                    iceConnectionState: newCall?.connection?.iceConnectionState
                });
                if (!iceCandidateErrorConnectionsRef.current.has(newCall?.connection)) {
                    reportIceError('webrtc_ice_gathering_timeout', {
                        message: 'Outgoing call terminated: ICE gathering did not complete within 2s.',
                        reason: outgoingCandidateCount === 0
                            ? 'No ICE candidates gathered in time - broken/unreachable TURN or network.'
                            : 'ICE gathering did not complete in time - PBX/TURN/network delay.',
                        direction: newCall.direction,
                        iceConnectionState: newCall?.connection?.iceConnectionState,
                        turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                        flowUuid: flowUuId || undefined,
                    });
                }
                terminateCallByError('Дзвінок не створено! Помилка мережі. Перевірте підключення, або спробуйте пізніше.');
            }, 2000);

            newCall.connection.onicegatheringstatechange = function () {
                const gatheringState = newCall.connection.iceGatheringState;

                if (gatheringState === 'complete') {
                    clearTimeout(iceTimeout);
                    if (outgoingRelayCandidateCount === 0) {
                        console.error('[ICE] call dropped', {
                            userId: user?.userId ?? 'unknown',
                            userName: user?.userName ?? 'unknown',
                            version: 'CC ' + Version,
                            ...(flowUuId ? {flowUuid: flowUuId} : {}),
                            direction: newCall.direction,
                            turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                            error: '[ICE] call dropped - no usable TURN/relay candidates gathered',
                        });
                        if (!iceCandidateErrorConnectionsRef.current.has(newCall?.connection)) {
                            reportIceError('webrtc_ice_no_relay_candidates', {
                                message: 'Outgoing call: ICE gathering completed with no relay (TURN) candidates.',
                                reason: 'No usable TURN/relay candidates - broken TURN credentials/URI or TURN unreachable.',
                                direction: newCall.direction,
                                turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                                flowUuid: flowUuId || undefined,
                            });
                        }
                    }
                }
            };

            newCall.on('accepted', () => {
                if (ringtoneAudioRef.current) {
                    ringtoneAudioRef.current.pause();
                    ringtoneAudioRef.current.currentTime = 0;
                }
                setStatus('Call Accepted');
                // playStartCallAudio();
            });

            newCall.on('ended', () => {
                handleCallSessionEnded(newCall);
            });

            newCall.on('failed', function (event: any) {
                logSipFailed(newCall, {cause: event?.cause, stage: 'outgoing'});
                setIncomingSession(null);
                setIncomingCall(false);
                setStatus('Call failed');
                setCall(null);
                closeDetails();
                setIsCallHold(false);
                setIsCallMute(false);
                setVisible(false);
                toggleHoldMusic(false);
                document.title = originalTitle;
                if (consultationCall) {
                    setActiveIndex(1);
                }
                playEndCallAudio();
            });
        } else {
            showError();
        }
    };

    const makeConsultationCall = (value?: any) => {
        if (ua && call !== null && phoneNumber.trim() !== '') {
            let userPhone = phoneNumber
            if (value) {
                userPhone = value;
            }


            const createUuidv4 = () => {
                return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
                    // @ts-ignore
                    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
                );
            }

            let flowUuId = createUuidv4();
            callOptions.extraHeaders = [];

            const groupIdForConsultation = getGroupIdForConsultation();
            if (groupIdForConsultation) {
                // @ts-ignore
                callOptions.extraHeaders.push(`X-group_flow_uuid: ${groupIdForConsultation}`);
                // @ts-ignore
                callOptions.extraHeaders.push(`X-campaign_id: consultation::0`);
                dispatch(clearRecallSession());
            }
            // @ts-ignore
            callOptions.extraHeaders.push(`X-flow_uuid: ${flowUuId}`);

            flowUuidRef.current = flowUuId;
            setFlowUuid(flowUuId)

            const destination = `sip:${userPhone}@${WWS_WEBRTC}`;
            // @ts-ignore
            const newCall = ua.call(destination, callOptions);
            attachLocalSdpCandidateFilter(newCall);
            setActiveIndex(1);

            setLastCallNumber(userPhone);
            setPhoneNumber('');

            newCall.on('connecting', () => {
                setStatus('Calling...');
            });

            let outgoingCandidateCount = 0;
            let outgoingRelayCandidateCount = 0;
            newCall.on('icecandidate', (e: any) => {
                if (!e?.candidate || e.candidate.type !== 'relay') {
                    return;
                }
                outgoingCandidateCount++;
                outgoingRelayCandidateCount++;
                const relayUrl = e.candidate.url;
                rememberSuccessfulRelayUrl(newCall?.connection, relayUrl);
                if (outgoingRelayCandidateCount === 1) {
                    e.ready();
                }
            });

            const terminateCallByError = (errorMessage: string) => {
                if (newCall) {
                    newCall.terminate();
                }
                setPhoneNumber('');
                endConsultationCall(false);
                document.title = originalTitle;
                if (!call && !consultationCall) {
                    trackStop();
                }
                if (call) {
                    setActiveIndex(0);
                }
                toastError?.current?.show({severity: 'error', summary: errorMessage, detail: '', life: 5000});
            };

            let iceTimeout = setTimeout(() => {
                if (outgoingRelayCandidateCount > 0) {
                    return;
                }
                console.error('[ICE] call dropped', {
                    userId: user?.userId ?? 'unknown',
                    userName: user?.userName ?? 'unknown',
                    version: 'CC ' + Version,
                    ...(flowUuId ? {flowUuid: flowUuId} : {}),
                    direction: newCall.direction,
                    iceConnectionState: newCall?.connection?.iceConnectionState,
                    turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                    error: outgoingCandidateCount === 0
                        ? '[ICE] call dropped - no candidates gathered in time (broken/unreachable candidates)'
                        : '[ICE] call dropped - ICE gathering did not complete in time',
                });
                logCall('dropped', {
                    reason: 'ice_gathering_timeout',
                    flowUuid: flowUuId,
                    direction: newCall.direction,
                    iceConnectionState: newCall?.connection?.iceConnectionState
                });
                if (!iceCandidateErrorConnectionsRef.current.has(newCall?.connection)) {
                    reportIceError('webrtc_ice_gathering_timeout', {
                        message: 'Outgoing call terminated: ICE gathering did not complete within 2s.',
                        reason: outgoingCandidateCount === 0
                            ? 'No ICE candidates gathered in time - broken/unreachable TURN or network.'
                            : 'ICE gathering did not complete in time - PBX/TURN/network delay.',
                        direction: newCall.direction,
                        iceConnectionState: newCall?.connection?.iceConnectionState,
                        turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                        flowUuid: flowUuId || undefined,
                    });
                }
                terminateCallByError('Дзвінок не створено! Помилка мережі. Перевірте підключення, або спробуйте пізніше.');
            }, 2000);

            newCall.connection.onicegatheringstatechange = function () {
                const gatheringState = newCall.connection.iceGatheringState;

                if (gatheringState === 'complete') {
                    clearTimeout(iceTimeout);
                    if (outgoingRelayCandidateCount === 0) {
                        console.error('[ICE] call dropped', {
                            userId: user?.userId ?? 'unknown',
                            userName: user?.userName ?? 'unknown',
                            version: 'CC ' + Version,
                            ...(flowUuId ? {flowUuid: flowUuId} : {}),
                            direction: newCall.direction,
                            turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                            error: '[ICE] call dropped - no usable TURN/relay candidates gathered',
                        });
                        if (!iceCandidateErrorConnectionsRef.current.has(newCall?.connection)) {
                            reportIceError('webrtc_ice_no_relay_candidates', {
                                message: 'Outgoing call: ICE gathering completed with no relay (TURN) candidates.',
                                reason: 'No usable TURN/relay candidates - broken TURN credentials/URI or TURN unreachable.',
                                direction: newCall.direction,
                                turnUrls: formatTurnUrls(callOptions?.pcConfig?.iceServers),
                                flowUuid: flowUuId || undefined,
                            });
                        }
                    }
                }
            };

            newCall.on('accepted', () => {
                if (ringtoneAudioRef.current) {
                    ringtoneAudioRef.current.pause();
                    ringtoneAudioRef.current.currentTime = 0;
                }
                setStatus('Call Accepted');
                // playStartCallAudio();
            });

            newCall.on('ended', () => {
                endConsultationCall(false);
                document.title = originalTitle;
                if (!call && !consultationCall) {
                    trackStop();
                }
                playEndCallAudio();
            });

            newCall.on('failed', function (event: any) {
                logSipFailed(newCall, {cause: event?.cause, stage: 'consultation'});
                endConsultationCall(false);
                document.title = originalTitle;
                if (!call && !consultationCall) {
                    trackStop();
                }
                playEndCallAudio();
            });
        } else {
            showError();
        }
    };

    useEffect(() => {
        if (!call && newCallInfo) {
            setCall(newCallInfo);
        } else if (call && newCallInfo) {
            setConsultationCall(newCallInfo);
        }
    }, [newCallInfo]);

    const playEndCallAudio = () => {
        if (endCallAudioPlayingRef.current) {
            return;
        }

        endCallAudioPlayingRef.current = true;

        if (endCallAudioRef?.current) {
            const audioElement = endCallAudioRef.current;
            audioElement.volume = ringtoneVolumeRef.current;
            audioElement.play().catch(() => {
                setConfirmDialogVisible(true);
            }).finally(() => {
                setTimeout(() => {
                    endCallAudioPlayingRef.current = false;
                }, 1000);
            });
        } else {
            endCallAudioPlayingRef.current = false;
        }
    };

    const playStartCallAudio = () => {
        if (startCallAudioRef?.current) {
            const audioElement = startCallAudioRef.current;
            audioElement.volume = 0.25;
            audioElement.currentTime = 0;
            setTimeout(() => {
                audioElement.play().catch((error) => {
                    console.log('Error playing start call audio:', error);
                });
            }, 100);
        } else {
            console.log('startCallAudioRef is null');
        }
    };

    const endCall = (isUserAction: boolean = true, hangupType: 'user' | 'refer' = 'user') => {
        const activeCall = hangupType === 'refer'
            ? (referSessionRef.current || callRef.current)
            : callRef.current;
        const hasPending = hasPendingIncomingCall();

        if (activeCall) {
            if (hangupType === 'refer') {
                sendReferHangupBye(activeCall);
            } else {
                safeTerminateSession(activeCall, undefined, hangupType);
            }
        }

        if (hangupType === 'refer') {
            clearReferHangupState();
        }

        if (consultationCall) {
            setActiveIndex(1);
        } else if (!hasPending) {
            dispatch({type: GET_CONSULTATION_FOR_SIP, payload: null});
        }

        if (hasPending) {
            cleanupAfterActiveCallEndedWithPendingIncoming();
            return;
        }

        setQueueId(null);
        dispatch({type: GET_MONITORING_FOR_SIP, payload: null});
        setSelectedRowData(null);
        setVisible(false);
        clearGroupIdForConsultation();
        clearGroupFlowUuid();
        if (activeCall) {
            setPhoneNumber('');
            closeDetails();
        }
        setQueueId(null);
        setQueueCallId(null);
        document.title = originalTitle;
        setStatus('Call Ended');
        if (!activeCall && !consultationCall) {
            trackStop();
        }
        playEndCallAudio();
    };

    const endConsultationCall = (
        isUserAction: boolean = true,
        hangupType: 'user' | 'refer' = 'user'
    ) => {
        if (!call) {
            dispatch({type: GET_MONITORING_FOR_SIP, payload: null});
            if (!call && !consultationCall) {
                trackStop();
            }
        }
        setActiveIndex(0);
        setSelectedConsultationData(null);
        dispatch({type: GET_CONSULTATION_FOR_SIP, payload: null});
        setConsultationCall(null);
        setQueueId(null);
        setSelectedConsultationData(null);
        setVisibleConsultation(false);
        closeConsultationDetails();
        if (consultationCall !== null) {
            const consultationSession = consultationCallRef.current ?? consultationCall;
            if (isUserAction) {
                safeTerminateSession(consultationSession, undefined, 'user');
            } else if (hangupType === 'refer' || referHangupActiveRef.current) {
                safeTerminateSession(consultationSession, undefined, 'refer');
            } else {
                try {
                    consultationSession.terminate();
                } catch (error) {
                    console.error('Error terminating consultation call:', error);
                }
            }
            setConsultationCall(null);
            setIsConsultationCallHold(false);
            setIsConsultationCallMute(false);
            setVisibleConsultation(false);
        }
        document.title = originalTitle;
        setStatus('Call Ended');
        playEndCallAudio();
    };

    const muteCall = () => {
        if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
        }
    };

    const toggleHoldMusic = (isHold: boolean) => {
        if (isHold) {
            //in case of a melody for the operator while waiting
            // if (holdMusicAudioRef.current) {
            //     holdMusicAudioRef.current.play().catch(error => {
            //         console.error('Ошибка воспроизведения музыки:', error);
            //     });
            // }
        } else {
            // if (holdMusicAudioRef.current) {
            //     holdMusicAudioRef.current.pause();
            //     holdMusicAudioRef.current.currentTime = 0;
            // }
        }
    };
    const tab1HeaderTemplate = (options: { onClick: React.MouseEventHandler<HTMLDivElement> | undefined; }) => {
        let direction = call?._request?.headers?.['X-Direction']?.[0].raw || monitoringSipData?.direction || callDirection;
        let callType = call?._request?.headers?.['X-Calltype']?.[0].raw || monitoringSipData?.callType;

        return (
            <div className="field col-12 mb-0 justify-content-center" style={{cursor: 'pointer'}}
                 onClick={options.onClick}>
                {<div className="field col-12 md:col-12 flex mb-0 align-items-center w-full justify-content-center">
                    <div className="mr-2">
                        {
                            callType === 'consultationExternal' ?
                                <div className="relative">
                                    <i className="pi pi-users p-2 border-circle text-white"
                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                        <div className="border-circle relative bg-primary"
                                             style={{width: '14px', height: '14px'}}>
                                            <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                        </div>
                                    </div>
                                </div> : callType === 'consultationInternal' ? <div className="relative">
                                    <i className="pi pi-users p-2 border-circle text-white"
                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                        <div className="border-circle relative bg-green-500"
                                             style={{width: '14px', height: '14px'}}>
                                            <i className="pi pi-arrow-down-left text-white absolute left-50 top-50 kk"
                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                        </div>
                                    </div>
                                </div> : callType === 'callbackExternal' ? (
                                    <div className="relative">
                                        <i className="pi pi-undo p-2 border-circle text-white"
                                           style={{background: 'rgb(253, 166, 16)'}}></i>
                                        <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                            <div className="border-circle relative bg-primary"
                                                 style={{width: '14px', height: '14px'}}>
                                                <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                                   style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                            </div>
                                        </div>
                                    </div>) : callType === 'callbackInternal' ? (
                                    <div className="relative">
                                        <i className="pi pi-undo p-2 border-circle text-white"
                                           style={{background: 'rgb(253, 166, 16)'}}></i>
                                        <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                            <div className="border-circle relative bg-green-500"
                                                 style={{width: '14px', height: '14px'}}>
                                                <i className="pi pi-arrow-down-left text-white absolute left-50 top-50 kk"
                                                   style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                            </div>
                                        </div>
                                    </div>) : callType === 'transferInternal' ? (
                                    <div className="relative">
                                        <i className="pi pi-reply p-2 bg-green-500 border-circle text-white"
                                           style={{transform: 'rotate(90deg) scaleX(1) scaleY(-1)'}}></i>
                                    </div>) : callType === 'transferExternal' ? (
                                    <div className="relative">
                                        <i className="pi pi-reply p-2 bg-primary border-circle text-white"
                                           style={{transform: 'rotate(90deg) scaleX(-1) scaleY(-1)'}}></i>
                                    </div>) : callType === 'internal' ? <div className="relative">
                                    <i className="pi pi-arrow-down-left p-2 border-circle bg-green-500 text-white"></i>
                                </div> : callType === 'pickupcall' ? (
                                    <div className="relative">
                                        <i className="pi pi-user p-2 border-circle text-white"
                                           style={{background: 'rgb(253, 166, 16)'}}></i>
                                    </div>
                                ) : callType === 'spycall' && spyCallMode === 'listen' ? (
                                    <div className="relative">
                                        <i className="pi pi-headphones p-2 border-circle text-white"
                                           style={{background: 'rgb(253, 166, 16)'}}></i>
                                    </div>
                                ) : callType === 'spycall' && spyCallMode !== 'listen' ? (
                                    <div className="relative">
                                        <i className="pi pi-volume-up p-2 border-circle text-white"
                                           style={{background: 'rgb(127, 72, 255)'}}></i>
                                    </div>) : callType === 'ivm' ? (
                                    <span
                                        className="inline-flex shrink-0 align-items-center justify-content-center bg-primary text-white font-bold border-circle"
                                        style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVM</span>
                                ) : callType === 'ivr' ? (
                                    <span
                                        className="inline-flex shrink-0 align-items-center justify-content-center bg-green-500 text-white font-bold border-circle"
                                        style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVR</span>
                                ) : monitoringSipData?.direction === 'c2c' && monitoringSipData?.callType === 'callback' ?
                                    <div className="relative">
                                        <i className="pi pi-undo p-2 border-circle text-white"
                                           style={{backgroundColor: "#fda610"}}></i>
                                    </div> : monitoringSipData?.direction === 'c2c' && monitoringSipData?.callType === 'external' ?
                                        <div className="relative">
                                            <i className="pi pi-arrow-up-right bg-primary p-2 border-circle text-white"></i>
                                        </div> :
                                        direction === 'transfer' ?
                                            <div className="relative">
                                                <i className="pi pi-reply p-2 bg-green-500 border-circle text-white"
                                                   style={{transform: 'rotate(90deg) scaleX(1) scaleY(-1)'}}></i>
                                            </div> : direction !== 'transfer' ?
                                                <div className="relative"><i
                                                    className="pi pi-arrow-up-right bg-primary p-2 border-circle text-white"></i>
                                                </div> : <div className="relative">
                                                    <i className="pi pi-arrow-down-left p-2 border-circle bg-green-500 text-white"></i>
                                                </div>}
                    </div>
                </div>}
            </div>
        );
    };

    const consultationHeaderTemplate = (options: { onClick: React.MouseEventHandler<HTMLDivElement> | undefined; }) => {
        let callType = consultationCall?._request?.headers?.['X-Calltype']?.[0].raw;

        return (
            <div className="field col-12 mb-0 justify-content-center" style={{cursor: 'pointer'}}
                 onClick={options.onClick}>
                {<div className="field col-12 md:col-12 flex mb-0 align-items-center w-full justify-content-center">
                    <div className="mr-2">
                        {callType === 'consultationExternal' ?
                            <div className="relative">
                                <i className="pi pi-users p-2 border-circle text-white"
                                   style={{background: 'rgb(253, 166, 16)'}}></i>
                                <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                    <div className="border-circle relative bg-primary"
                                         style={{width: '14px', height: '14px'}}>
                                        <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                           style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                    </div>
                                </div>
                            </div> : callType === 'consultationInternal' ? <div className="relative">
                                <i className="pi pi-users p-2 border-circle text-white"
                                   style={{background: 'rgb(253, 166, 16)'}}></i>
                                <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                    <div className="border-circle relative bg-green-500"
                                         style={{width: '14px', height: '14px'}}>
                                        <i className="pi pi-arrow-down-left text-white absolute left-50 top-50 kk"
                                           style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                    </div>
                                </div>
                            </div> : callType === 'ivm' ? (
                                <span
                                    className="inline-flex shrink-0 align-items-center justify-content-center bg-primary text-white font-bold border-circle"
                                    style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVM</span>
                            ) : callType === 'ivr' ? (
                                <span
                                    className="inline-flex shrink-0 align-items-center justify-content-center bg-green-500 text-white font-bold border-circle"
                                    style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVR</span>
                            ) : callType === 'spycall' ? (
                                    <div className="relative">
                                        <i className="pi pi-headphones p-2 border-circle text-white"
                                           style={{background: 'rgb(253, 166, 16)'}}></i>
                                    </div>
                                ) :
                                <div className="relative">
                                    <i className="pi pi-users p-2 border-circle text-white"
                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                        <div className="border-circle relative bg-primary"
                                             style={{width: '14px', height: '14px'}}>
                                            <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                        </div>
                                    </div>
                                </div>}
                    </div>
                </div>}
            </div>
        );
    };

    // const tab3HeaderTemplate = (options: { onClick: React.MouseEventHandler<HTMLDivElement> | undefined; }) => {
    //     return (
    //         <div className="field col-12 mb-0 justify-content-center" style={{cursor: 'pointer'}}
    //              onClick={options.onClick}>
    //             {<div className="field col-12 md:col-12 flex mb-0 align-items-center w-full justify-content-center">
    //                 <div className="mr-2">
    //                     {!incomingCall ? <div className="relative">
    //                             {callDirection === 'transfer' ?
    //                                 <i className="pi pi-reply p-2 bg-green-500 border-circle text-white"
    //                                    style={{transform: 'rotate(90deg) scaleX(1) scaleY(-1)'}}></i> :
    //                                 <i className="pi pi-arrow-up-right bg-primary p-2 border-circle text-white"></i>}
    //                         </div> :
    //                         <div className="relative">
    //                             <i className="pi pi-arrow-down-left p-2 border-circle bg-green-500 text-white"></i>
    //                         </div>}
    //                 </div>
    //                 <div>3</div>
    //             </div>}
    //         </div>
    //     )
    // };

    const holdCall = () => {
        if (call !== null) {
            call.hold();
            setIsCallHold(true);
            toggleHoldMusic(true);
        }
    };

    const unHoldCall = () => {
        if (call !== null) {
            call.unhold();
            setIsCallHold(false);
            toggleHoldMusic(false);
            setCallHoldDuration(0);
            if (consultationCall !== null) {
                holdConsultationCall();
            }
        }
    };

    const objectToEscapedString = (obj: { timeStart?: Date; timeEnd?: Date; call_id: any; flow_uuid: any; id: number; Sip_auth_username: null; }) => {
        const jsonString = JSON.stringify(obj, null, 2);

        return jsonString
            .replace(/"/g, '\\"')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r');
    }

    const muteAudio = () => {
        if (call !== null) {
            call.mute({audio: true, video: false});
            setIsCallMute(true);
            const newObject = {
                ...muteCallData,
                timeStart: new Date(),
                id: muteId,
                call_id: getCallIdForCallEvent(flowUuid, call, incomingSession),
                flow_uuid: flowUuid,
            };

            const escapedString = objectToEscapedString(newObject);

            dispatch(sendCallEvents('Mute', '"' + escapedString + '"', jwtToken?.jwtToken))
        }
    };

    const unmuteAudio = () => {
        if (call !== null) {
            call.unmute({audio: true, video: false});
            setIsCallMute(false);
            setCallMuteDuration(0);
            const newObject = {
                ...unMuteCallData,
                id: muteId,
                timeEnd: new Date(),
                call_id: getCallIdForCallEvent(flowUuid, call, incomingSession),
                flow_uuid: flowUuid,
            };

            const escapedString = objectToEscapedString(newObject);

            dispatch(sendCallEvents('UnMute', '"' + escapedString + '"', jwtToken?.jwtToken))
        }
    };

    const muteConsultationAudio = () => {
        if (consultationCall !== null) {
            consultationCall.mute({audio: true, video: false});
            setIsConsultationCallMute(true);
            const newObject = {
                ...muteCallData,
                timeStart: new Date(),
                id: muteConsultationId,
                call_id: getCallIdForCallEvent(flowUuid, consultationCall),
                flow_uuid: flowUuid,
            };

            const escapedString = objectToEscapedString(newObject);

            dispatch(sendCallEvents('Mute', '"' + escapedString + '"', jwtToken?.jwtToken, true))
        }
    };

    const unmuteConsultationAudio = () => {
        if (consultationCall !== null) {
            consultationCall.unmute({audio: true, video: false});
            setIsConsultationCallMute(false);
            setConsultationCallMuteDuration(0);
            const newObject = {
                ...unMuteCallData,
                id: muteConsultationId,
                timeEnd: new Date(),
                call_id: getCallIdForCallEvent(flowUuid, consultationCall),
                flow_uuid: flowUuid,
            };

            const escapedString = objectToEscapedString(newObject);

            dispatch(sendCallEvents('UnMute', '"' + escapedString + '"', jwtToken?.jwtToken, true))
        }
    };

    const holdConsultationCall = () => {
        if (consultationCall !== null) {
            consultationCall.hold();
            setIsConsultationCallHold(true);
            toggleHoldMusic(true);
        }
    };

    const unHoldConsultationCall = () => {
        if (consultationCall !== null) {
            consultationCall.unhold();
            setIsConsultationCallHold(false);
            toggleHoldMusic(false);
            setConsultationCallHoldDuration(0);
            if (call !== null) {
                holdCall();
            }
        }
    };

    const showError = () => {
        // @ts-ignore
        toast?.current?.show({severity: 'error', summary: 'Поле не може бути порожнім', detail: '', life: 3000});
    }

    const showTurnDataError = () => {
        // @ts-ignore
        toast?.current?.show({severity: 'error', summary: 'Turn Data Error', detail: '', life: 3000});
    }

    const reportIceError = (
        errorCode: string,
        details: {
            message: string;
            reason?: string;
            direction?: string;
            iceConnectionState?: string;
            turnUrls?: string;
            suspectedCause?: string;
            brokenCandidates?: Array<{ url?: string; errorCode?: number | string; errorText?: string; address?: string | null; port?: number | null }>;
            flowUuid?: string;
        },
        dedupeKey?: string,
        bypassDedupe?: boolean
    ) => {
        if (!bypassDedupe && !shouldReportCriticalError(dedupeKey ?? errorCode)) return;
        dispatch(
            reportCriticalError(
                {
                    code:
                        errorCode + "\n" +
                        "userId: " + (user?.userId ?? "unknown") + "\n" +
                        "userName: " + (user?.userName ?? "unknown") + "\n" +
                        "version: CC " + Version + "\n" +
                        (details.flowUuid ? "flowUuid: " + details.flowUuid + "\n" : "") +
                        (details.direction ? "direction: " + details.direction + "\n" : "") +
                        (details.iceConnectionState ? "iceConnectionState: " + details.iceConnectionState + "\n" : "") +
                        (details.turnUrls ? "turnUrls: " + details.turnUrls + "\n" : "") +
                        (details.suspectedCause ? "suspectedCause: " + details.suspectedCause + "\n" : "") +
                        (details.brokenCandidates && details.brokenCandidates.length
                            ? "brokenCandidatesCount: " + details.brokenCandidates.length + "\n" +
                              "brokenCandidates:\n" +
                              details.brokenCandidates.map((candidate, index) =>
                                  "  [" + (index + 1) + "] url: " + (candidate.url ?? "unknown") +
                                  ", errorCode: " + (candidate.errorCode ?? "unknown") +
                                  ", errorText: " + (candidate.errorText ?? "unknown") +
                                  ", address: " + (candidate.address ?? "null") +
                                  ", port: " + (candidate.port ?? "null")
                              ).join("\n") + "\n"
                            : "") +
                        "message: " + details.message + "\n"
                },
                jwtToken?.jwtToken
            )
        );
    }

    // Запуск probe TURN-серверов с дедупом по сигнатуре конфига. Для одного и того же
    // набора TURN (urls + username) probe и webrtc_turn_probe_failed отправятся не более
    // одного раза, даже если функция вызвана из нескольких мест (registered / turninfo)
    // или turninfo пришёл повторно.
    const runTurnProbe = (iceServers: any[]) => {
        if (!iceServers?.length) return;
        const signature = JSON.stringify(
            iceServers.map((server: any) => ({ urls: server?.urls, username: server?.username }))
        );
        if (turnProbeSignatureRef.current === signature) return;
        turnProbeSignatureRef.current = signature;
        // Временно не шлём webrtc_turn_probe_failed: probe крутится на registered/turninfo
        // без звонка и шумит в ErrorEventsManager (no-relay на stage TURN).
        // Ошибки во время звонка (no_relay / gathering_timeout / ice_call_dropped) остаются.
        void probeTurnServers(iceServers, {
            userId: user?.userId,
            userName: user?.userName,
            version: Version,
            flowUuid: flowUuid || undefined,
        });
        // .then((result) => {
        //     if (!result.ok) {
        //         reportIceError('webrtc_turn_probe_failed', {
        //             message: 'TURN probe failed (' + (result.reason ?? 'unknown') + ') - no relay candidate received from TURN.',
        //             turnUrls: formatTurnUrls(iceServers),
        //             flowUuid: flowUuid || undefined,
        //         }, 'webrtc_turn_probe_failed_' + signature);
        //     }
        // });
    };

    // Диагностика ICE. Ошибки candidate (напр. errorCode 701 — сервер недоступен по
    // данному транспорту) часто возникают из-за VPN, который режет UDP. Наблюдение:
    // у правдивой ошибки (сломанный/недоступный TURN, без VPN) iceErr.address === null,
    // а при VPN трафик уходит через tunnel-интерфейс и address заполнен.
    //
    // Логика (чисто параллельное наблюдение, звонок НЕ трогаем и не рвём):
    //   - address заполнен -> транзиентная (VPN/сеть), игнорируем;
    //   - address === null -> копим в Map по ключу URI; повторные ошибки по тому же URI
    //     игнорируем (не более одной записи на хост);
    //   - когда TURN-цикл завершён (icegatheringstate === 'complete') или звонок разрешился
    //     (connected/completed/failed), одним алертом отправляем накопленные URI.
    //   - turn_partial_failure: часть TURN хостов упала, но relay с другого есть.
    //     Не шлём, если все настроенные хосты помечены сломанными при наличии relay
    //     (ложное inferred, звонок при этом проходит).
    //     Не шлём inferred-only: нет реального icecandidateerror, а «нет relay с хоста»
    //     часто значит, что ICE уже connected после первого relay (e.ready()), второй
    //     TURN просто не успели дождаться.
    // Это эвристика (на части конфигураций Chrome address может быть null и из-за mDNS),
    // поэтому в отчёт кладём сырые address/port для перепроверки на бэкенде. Реальные
    // тотальные отказы также ловятся независимо: no_relay/gathering_timeout (makeCall)
    // и webrtc_ice_call_dropped при iceConnectionState === 'failed'.
    const attachIceErrorTracking = (
        connection: any,
        ctx: { direction?: string; turnUrls?: string; flowUuid?: string; iceServers?: unknown }
    ) => {
        if (!connection || connection.__iceErrorTrackingAttached) return;
        connection.__iceErrorTrackingAttached = true;

        const configuredKeys = getConfiguredIceUrlKeys(ctx.turnUrls, ctx.iceServers);
        let relayCandidateCount = 0;
        let reported = false;
        const successfulRelayUrls = new Set<string>();
        const brokenByUri = new Map<string, { url?: string; errorCode?: number | string; errorText?: string; address?: string | null; port?: number | null }>();
        let flushTimer: ReturnType<typeof setTimeout> | null = null;

        const clearFlushTimer = () => {
            if (flushTimer) {
                clearTimeout(flushTimer);
                flushTimer = null;
            }
        };

        const flushBrokenUris = (trigger: string) => {
            clearFlushTimer();
            // ICE часто становится connected до конца gathering (первый relay + e.ready()).
            // Infer «хост молчит» в этот момент ложно помечает второй TURN как сломанный.
            if (relayCandidateCount > 0 && trigger === 'gathering complete') {
                const mergedRelayUrls = new Set(successfulRelayUrls);
                const externalRelayUrls = (connection as any).__successfulRelayUrls as Set<string> | undefined;
                externalRelayUrls?.forEach((url) => mergedRelayUrls.add(url));
                inferMissingTurnHosts(configuredKeys, mergedRelayUrls, relayCandidateCount, brokenByUri);
            }
            if (reported || brokenByUri.size === 0) return;

            const isPartialFailure = relayCandidateCount > 0;
            const brokenCandidates = Array.from(brokenByUri.values());
            if (isPartialFailure) {
                const configuredHosts = uniqueIceHosts(configuredKeys);
                const brokenHosts = uniqueIceHosts(Array.from(brokenByUri.keys()));
                const allConfiguredHostsBroken = configuredHosts.size > 0 &&
                    Array.from(configuredHosts).every((host) => brokenHosts.has(host));
                if (allConfiguredHostsBroken) {
                    reported = true;
                    console.warn('[ICE] skip turn_partial_failure: all TURN hosts marked broken while relay candidates exist', {
                        trigger,
                        direction: ctx.direction,
                        turnUrls: ctx.turnUrls,
                        flowUuid: ctx.flowUuid,
                        relayCandidateCount,
                        brokenCandidates,
                    });
                    return;
                }
                const hasRealCandidateError = brokenCandidates.some(
                    (candidate) => candidate.errorCode !== 'inferred'
                );
                if (!hasRealCandidateError) {
                    reported = true;
                    console.warn('[ICE] skip turn_partial_failure: inferred missing TURN host while another relay was used', {
                        trigger,
                        direction: ctx.direction,
                        turnUrls: ctx.turnUrls,
                        flowUuid: ctx.flowUuid,
                        relayCandidateCount,
                        brokenCandidates,
                    });
                    return;
                }
            }

            reported = true;
            iceCandidateErrorConnectionsRef.current.add(connection);
            reportIceError('webrtc_ice_candidate_error', {
                message: 'ICE candidate error(s) after TURN cycle (' + trigger + '): ' +
                    brokenByUri.size + ' TURN host(s) failed' +
                    (isPartialFailure ? ' while another relay was used' : ' with address null') +
                    '. relayCandidates: ' + relayCandidateCount + '.',
                reason: isPartialFailure
                    ? 'One or more TURN hosts failed while the call used another relay candidate - check blocked/unreachable TURN host(s), credentials, ports or network.'
                    : 'STUN/TURN candidate(s) failed with no local address - genuine broken/unreachable TURN host(s), check TURN credentials/URI, ports or network.',
                direction: ctx.direction,
                turnUrls: ctx.turnUrls,
                suspectedCause: isPartialFailure ? 'turn_partial_failure' : 'turn_unreachable',
                brokenCandidates,
                flowUuid: ctx.flowUuid,
            }, undefined, true);
        };

        connection.addEventListener('icecandidateerror', (iceErr: any) => {
            const address = iceErr?.address ?? null;
            const port = iceErr?.port ?? null;
            if (address != null) return;

            const uriKey = iceErr?.url ?? 'unknown';
            if (brokenByUri.has(uriKey)) return;
            brokenByUri.set(uriKey, {
                url: iceErr?.url,
                errorCode: iceErr?.errorCode,
                errorText: iceErr?.errorText,
                address,
                port,
            });
            if (!flushTimer) {
                flushTimer = setTimeout(() => flushBrokenUris('safety timeout'), 3000);
            }
        });

        connection.addEventListener('icecandidate', (event: any) => {
            if (event?.candidate?.type === 'relay') {
                relayCandidateCount++;
                const relayUrl = event?.url ?? event?.candidate?.url;
                if (relayUrl) {
                    successfulRelayUrls.add(String(relayUrl).trim().toLowerCase());
                }
            }
        });

        connection.addEventListener('icegatheringstatechange', () => {
            if (connection.iceGatheringState === 'complete') {
                flushBrokenUris('gathering complete');
            }
        });

        connection.addEventListener('iceconnectionstatechange', () => {
            const iceConnectionState = connection.iceConnectionState;
            if (iceConnectionState === 'connected' || iceConnectionState === 'completed') {
                flushBrokenUris('connection ' + iceConnectionState);
            }
            if (iceConnectionState === 'failed') {
                flushBrokenUris('connection failed');
                console.error('[ICE] call dropped', {
                    userId: user?.userId ?? 'unknown',
                    userName: user?.userName ?? 'unknown',
                    version: 'CC ' + Version,
                    ...(ctx.flowUuid ? { flowUuid: ctx.flowUuid } : {}),
                    direction: ctx.direction,
                    iceConnectionState,
                    turnUrls: ctx.turnUrls,
                    error: relayCandidateCount === 0
                        ? '[ICE] call dropped - no candidates gathered'
                        : '[ICE] call dropped - connection failed',
                });
                logCall('dropped', {
                    reason: 'ice_connection_failed',
                    flowUuid: ctx.flowUuid,
                    direction: ctx.direction,
                    iceConnectionState
                });
                reportIceError('webrtc_ice_call_dropped', {
                    message: 'Call dropped: ICE connection state changed to "failed".',
                    reason: relayCandidateCount === 0
                        ? 'No ICE candidates gathered - broken/unreachable TURN or network.'
                        : 'ICE connectivity checks failed - no working candidate pair (TURN/network).',
                    direction: ctx.direction,
                    iceConnectionState,
                    turnUrls: ctx.turnUrls,
                    flowUuid: ctx.flowUuid,
                });
            }
        });
    };

    const handleClickAdd = (number: string) => {
        setPhoneNumber(prevNumber => prevNumber + number);

    };
    const handleClickDelete = () => {
        setPhoneNumber(prevNumber => prevNumber.slice(0, -1));
    };

    const handleClickRemove = () => {
        if (lastCallNumber) {
            setPhoneNumber(lastCallNumber);
            makeCall();
        }
    };

    const openDetails = (id: any, isConsultation?: boolean) => {
        dispatch(getEventMonitorToUserDetail(id, jwtToken?.jwtToken, true, isConsultation));
        setVisible(true);
        setVisibleConsultation(true);
        setSelectedRowData(id);
    }

    const closeDetails = () => {
        dispatch(clearMonitoringSipDetails());
        setVisible(false);
        setSelectedRowData(null);
        if (!callRef.current && !consultationCallRef.current) {
            clearGroupIdForConsultation();
            clearGroupFlowUuid();
        }
    }

    const closeConsultationDetails = () => {
        setVisibleConsultation(false);
        setSelectedConsultationData(null);
    }

    const acceptCall = () => {
        const sessionToAccept = pendingIncomingSessionRef.current || incomingSessionRef.current;
        if (!sessionToAccept) {
            return;
        }
        logCall('incoming_accepted', {
            direction: sessionToAccept?.direction,
            callId: getCallIdForCallEvent(flowUuidRef.current, sessionToAccept),
            flowUuid: getSessionFlowUuid(sessionToAccept, flowUuidRef.current)
        });
        if (isAcceptingCallRef.current) {
            return;
        }
        if (
            sessionToAccept.status === JS_SIP_STATUS_TERMINATED ||
            sessionToAccept.status === JS_SIP_STATUS_CONFIRMED
        ) {
            return;
        }

        isAcceptingCallRef.current = true;
        clearGroupIdForConsultation();
        clearGroupFlowUuid();

        if (hasAnotherActiveSession(sessionToAccept)) {
            terminatePreviousCallsBeforeAccept(sessionToAccept);
        }

        setPendingIncomingSession(null);
        pendingIncomingSessionRef.current = null;
        setShowDialog(false);
        setIncomingCall(false);
        setIncomingSession(null);
        incomingSessionRef.current = null;
        setCall(sessionToAccept);
        setCallDirection(getSessionDirection(sessionToAccept));
        setFlowUuid(getSessionFlowUuid(sessionToAccept) ?? '');
        updateGroupFlowUuid(sessionToAccept?._request?.headers?.['X-Group-Flow-Uuid']?.[0]?.raw);
        setIsConsultationCall(false);
        applyPendingCallDetails();

        sessionToAccept.on('failed', (event: any) => {
            logSipFailed(sessionToAccept, {cause: event?.cause, stage: 'accept'});
            isAcceptingCallRef.current = false;
            if (hasPendingIncomingCall()) {
                cleanupAfterActiveCallEndedWithPendingIncoming();
                return;
            }
            const failedType = sessionToAccept?._request?.headers?.['X-Calltype']?.[0]?.raw;
            if (failedType === 'spycall') {
                dispatch(setSpySessionId(null));
                setSpyCallMode("listen");
            }
            setIncomingCall(false);
            setIncomingSession(null);
            setCall(null);
            closeDetails();
            setIsCallHold(false);
            setIsCallMute(false);
            setVisible(false);
            toggleHoldMusic(false);
            playEndCallAudio();
        });

        sessionToAccept.on('confirmed', () => {
            setStatus('Call confirmed');
            // playStartCallAudio();
        });

        sessionToAccept.on('canceled', () => {
            setStatus('Call canceled');
            playEndCallAudio();
        });

        attachLocalSdpCandidateFilter(sessionToAccept);
        let incomingReadySent = false;
        sessionToAccept.on('icecandidate', (e: any) => {
            if (!e?.candidate || e.candidate.type !== 'relay') {
                return;
            }
            if (!incomingReadySent) {
                incomingReadySent = true;
                e.ready();
            }
        });

        sessionToAccept.on('accepted', () => {
            isAcceptingCallRef.current = false;
            stopIncomingRingtone();
            setStatus('Call Accepted');
            if (sessionToAccept?._request?.headers?.['X-Calltype']?.[0]?.raw === 'spycall') {
                dispatch(setSpyCallAnsweredTick());
                closeDetailVisible();
            }
        });

        sessionToAccept.on('ended', () => {
            isAcceptingCallRef.current = false;
            handleCallSessionEnded(sessionToAccept);
        });

        setLastCallNumber(sessionToAccept?.remote_identity.uri.user);

        let incomingSessionInfo = sessionToAccept?._request?.headers?.['X-Turn']?.[0]?.raw;

        let incomingCallOptions = {...callOptions};
        if (incomingSessionInfo) {
                let message: any = null;
                try {
                    message = JSON.parse(incomingSessionInfo);
                } catch {
                    const errorCode = "webrtc_incoming_x_turn_parse_error";
                    if (shouldReportCriticalError(errorCode)) {
                        dispatch(
                            reportCriticalError(
                                {
                                    code:
                                        errorCode + "\n" +
                                        "userId: " + (user?.userId ?? "unknown") + "\n" +
                                        "userName: " + (user?.userName ?? "unknown") + "\n" +
                                        "version: CC " + Version + "\n" +
                                        "headerPreview: " + String(incomingSessionInfo ?? "").slice(0, 240).replace(/\s+/g, " ") + "\n" +
                                        "dateTime: " + new Date().toISOString() + "\n" +
                                        "device: " + (device ?? "unknown") + "\n" +
                                        "userAgent: " + (userAgent ?? "unknown") + "\n" +
                                        "timezone: " + Intl.DateTimeFormat().resolvedOptions().timeZone + "\n" +
                                        "visibilityState: " + (typeof document !== "undefined" ? document.visibilityState : "unknown") + "\n" +
                                        "connectionType: " + (internetConnection?.effectiveType ?? "unknown") + "\n" +
                                        "downlink: " + (internetConnection?.downlink ?? "unknown") + "\n" +
                                        "url: " + (typeof window !== "undefined" ? window.location.href : "unknown") + "\n" +
                                        "message: On acceptCall (incoming answer), parsing INVITE header X-Turn raw value as JSON for TURN (turninfo).\n" +
                                        "reason: JSON.parse failed - header empty, truncated, or not JSON.\n"
                                },
                                jwtToken?.jwtToken
                            )
                        );
                    }
                }
                const incomingData2 = Array.isArray(message?.data2) ? message.data2 : null;
                if (message) {
                    if (message.cmd === 'turninfo') {
                        if (incomingData2?.length) {
                            incomingCallOptions.pcConfig.iceServers = incomingData2
                                .filter((item: any) => item?.uri)
                                .map((item: any) => ({
                                    urls: item.uri,
                                    username: item?.user,
                                    credential: item?.pass
                                }));
                        } else if (message.data) {
                            const protocol = message.data.secure ? "turns" : "turn";
                            const urls = `${protocol}:${message.data.ip}:${message.data.port}?transport=${message.data.transport}`;
                            const iceServer = {
                                urls,
                                username: message?.data?.user,
                                credential: message?.data?.pass
                            };
                            incomingCallOptions.pcConfig.iceServers = [];
                            incomingCallOptions?.pcConfig?.iceServers.push(iceServer);
                        } else {
                            const errorCode = "webrtc_incoming_x_turn_data_invalid";
                            if (shouldReportCriticalError(errorCode)) {
                                dispatch(
                                    reportCriticalError(
                                        {
                                            code:
                                                errorCode + "\n" +
                                                "userId: " + (user?.userId ?? "unknown") + "\n" +
                                                "userName: " + (user?.userName ?? "unknown") + "\n" +
                                                "version: CC " + Version + "\n" +
                                                "cmd: " + (message?.cmd ?? "unknown") + "\n" +
                                                "dateTime: " + new Date().toISOString() + "\n" +
                                                "device: " + (device ?? "unknown") + "\n" +
                                                "userAgent: " + (userAgent ?? "unknown") + "\n" +
                                                "timezone: " + Intl.DateTimeFormat().resolvedOptions().timeZone + "\n" +
                                                "visibilityState: " + (typeof document !== "undefined" ? document.visibilityState : "unknown") + "\n" +
                                                "connectionType: " + (internetConnection?.effectiveType ?? "unknown") + "\n" +
                                                "downlink: " + (internetConnection?.downlink ?? "unknown") + "\n" +
                                                "url: " + (typeof window !== "undefined" ? window.location.href : "unknown") + "\n" +
                                                "message: On acceptCall, X-Turn JSON parsed OK, cmd is turninfo, before applying TURN to incoming answer pcConfig (expected message.data).\n" +
                                                "reason: message.data is missing or falsy - turninfo without server/ip/port/credentials in INVITE X-Turn.\n"
                                        },
                                        jwtToken?.jwtToken
                                    )
                                );
                            }
                        }
                    }
                    const audioFromData2 = incomingData2?.length
                        ? parseTurnAudioString(incomingData2.find((item: any) => item?.audio)?.audio)
                        : null;
                    if (audioFromData2) {
                        incomingCallOptions.mediaConstraints.audio = {
                            ...incomingCallOptions.mediaConstraints.audio,
                            ...audioFromData2
                        };
                    } else if (message.audio) {
                        incomingCallOptions.mediaConstraints.audio = {
                            ...incomingCallOptions.mediaConstraints.audio,
                            ...Object.fromEntries(Object.entries(message.audio).map(([key, value]) => [key, value]))
                        };
                    }
                }
            }

        try {
            sessionToAccept.answer(incomingCallOptions);
            if (sessionToAccept?.connection) {
                const sessionFlowUuid =
                    getSessionFlowUuid(sessionToAccept, flowUuidRef.current);
                attachIceErrorTracking(sessionToAccept.connection, {
                    direction: sessionToAccept.direction,
                    turnUrls: formatTurnUrls(incomingCallOptions?.pcConfig?.iceServers),
                    iceServers: incomingCallOptions?.pcConfig?.iceServers,
                    flowUuid: sessionFlowUuid,
                });
            }
            sessionToAccept.connection.addEventListener('addstream', (e: any) => {
                if (audioRef.current) {
                    audioRef.current.srcObject = e.stream;
                    audioRef.current.play();
                }
            });
        } catch (error: any) {
            isAcceptingCallRef.current = false;
            logCall('answer_failed', {message: error?.message || String(error)});
            console.error('Error answering call:', error);
        }
    };

    const declineCall = () => {
        const sessionToDecline = pendingIncomingSessionRef.current || incomingSessionRef.current;
        if (sessionToDecline) {
            safeTerminateSession(sessionToDecline, getHangupHeader('user'));
        }
        if (pendingIncomingSession) {
            clearPendingIncomingState();
        } else {
            setIncomingSession(null);
            setIncomingCall(false);
            stopIncomingRingtone();
            document.title = originalTitle;
        }
        setShowDialog(false);
    };

    const handleInputChange = (e: any) => {
        let inputValue;
        if (e?.query) {
            inputValue = e?.query;
        } else {
            inputValue = e;
        }

        setPhoneNumber(inputValue);
        if (inputValue && !webRTCError && isOnline && strengthClass !== 'weak') {
            dispatch(getUsersWebRTC(inputValue, jwtToken?.jwtToken));
        }
    }

    useEffect(() => {
        if (call) {
            if (isConsultationCall) {
                setReferCall(false);
            } else {
                setReferCall(true);
            }
        } else {
            setReferCall(false);
        }
    }, [autocompleteChange]);

    const itemTemplate = (item: any) => {
        const isUser = !item.id;
        const isInput = item?.isInput;
        const iconName = isUser && !isInput ? "pi pi-user" : isInput ? "pi pi-mobile" : "pi pi-database";
        const iconPhone = "pi pi-phone";
        const itemLabel = isUser ? item?.userName : item?.name;
        const buttonDisabled = isUser ? !item?.isLogin || !activeSession : !activeSession;
        const buttonClass = `autocomplete-item border-circle ${(item?.isLogin && isUser) || (!isUser && activeSession) ? "text-white" : "p-button-secondary bg-gray-500"} ${(call || incomingSession) && "p-button-refer"}`;
        const itemQueueId = item?.id || null;
        const itemValue = isUser ? item?.userName : item?.name;

        const handleItemButtonClick = (e: React.MouseEvent, isConsultation: boolean) => {
            e.stopPropagation();
            setIsConsultationCall(isConsultation);
            setQueueCallId(itemQueueId);
            const isRefer = !isConsultation && !!(call || incomingSession);
            checkUser(itemValue, isRefer, itemQueueId);
            autoCompleteRef.current?.hide();
        };

        const preventFocusLoss = (e: React.MouseEvent) => {
            e.preventDefault();
        };

        return (
            <>
                <div className={`flex align-items-center justify-content-between`}>
                    <div className={`mr-${isUser ? '2' : '4'}`}><i className={`${iconName} mr-2`}></i>{itemLabel}
                    </div>
                    {!call && !incomingSession ? <Button
                            icon={iconPhone}
                            disabled={buttonDisabled || !isOnline}
                            className={buttonClass}
                            onMouseDown={preventFocusLoss}
                            onClick={(e) => handleItemButtonClick(e, false)}
                            style={{width: '30px', height: '30px'}}
                        ></Button> :
                        <div>
                            <Button
                                icon={iconPhone}
                                disabled={buttonDisabled || !isOnline}
                                onMouseDown={preventFocusLoss}
                                onClick={(e) => handleItemButtonClick(e, false)}
                                style={{width: '30px', height: '30px'}}
                                className={buttonClass}
                            >
                                <img className="arrow-refer" src={"assets/layout/images/arrow-right.png"}
                                     alt="avatar"/>
                            </Button>
                            <Button
                                icon={'pi pi-users'}
                                disabled={buttonDisabled || !isOnline}
                                onMouseDown={preventFocusLoss}
                                onClick={(e) => handleItemButtonClick(e, true)}
                                style={{width: '30px', height: '30px', background: 'rgb(253, 166, 16)'}}
                                className={`autocomplete-item border-circle ${(item?.isLogin && isUser) || (!isUser && activeSession) ? "text-white" : "p-button-secondary bg-gray-500"} ml-2 border-none`}
                            >
                            </Button>
                        </div>
                    }
                </div>
            </>
        );
    }

    const accept = () => {
        if (incomingSession) {
            playIncomingRingtone();
        }
    }

    const buildReferExtraHeaders = (options?: { includeTransferCampaign?: boolean }) => {
        const extraHeaders: string[] = [
            `X-flow_uuid: ${flowUuid}`,
            getHangupHeader('refer'),
        ];

        if (options?.includeTransferCampaign) {
            extraHeaders.push(`X-campaign_id: transfer::0`);
        }

        const groupFlowUuid = getGroupFlowUuid();
        if (groupFlowUuid) {
            extraHeaders.push(`X-group_flow_uuid: ${groupFlowUuid}`);
            dispatch(clearRecallSession());
            clearGroupFlowUuid();
        }

        return extraHeaders;
    };

    const handleReferCall = (value: any, queueId: number | null) => {
        let targetValue = queueId ? queueId : value;
        const targetUri = `sip:${targetValue}@${WWS_WEBRTC}`;
        const activeSession = getActiveReferSession();
        if (activeSession) {
            markReferHangupActive(activeSession);
            const referSubscriber = activeSession.refer(targetUri, {
                extraHeaders: buildReferExtraHeaders()
            });

            if (referSubscriber) {
                sendReferHangupBye(activeSession);

                referSubscriber.on('requestSucceeded', () => {
                    sendReferHangupBye(activeSession);
                    endCall(false, 'refer');
                });

                referSubscriber.on('requestFailed', (e: any) => {
                    clearReferHangupState();
                    console.log('REFER не удался:', e);
                });

                referSubscriber.on('accepted', () => {
                    sendReferHangupBye(activeSession);
                });
            } else {
                clearReferHangupState();
            }
        }
    };

    const handleReferToConsultationCall = (value: any) => {
        const targetUri = `sip:${value}@${WWS_WEBRTC}`;
        const activeSession = getActiveReferSession();
        if (activeSession) {
            markReferHangupActive(activeSession, true);
            const referSubscriber = activeSession.refer(targetUri, {
                extraHeaders: buildReferExtraHeaders({ includeTransferCampaign: true })
            });

            if (referSubscriber) {
                sendReferHangupBye(activeSession);

                referSubscriber.on('requestSucceeded', () => {
                    sendReferHangupBye(activeSession);
                    endCall(false, 'refer');
                    endConsultationCall(false, 'refer');
                    setConsultationReferCall(false);
                    clearReferHangupState();
                });

                referSubscriber.on('requestFailed', (e: any) => {
                    clearReferHangupState();
                    console.log('REFER не удался:', e);
                    setConsultationReferCall(false);
                });

                referSubscriber.on('accepted', () => {
                    sendReferHangupBye(activeSession);
                });
            } else {
                clearReferHangupState();
            }
        }
    };

    const normalizePhone = (phone: string | any) => {
        if (phone.startsWith('38')) {
            return phone.substring(2);
        }
        return phone;
    }
    const extractUsername = (sipString: any): string => {
        const match = sipString?.match(/<sip:([^@]+)@/);
        return match ? match[1] : '';
    };

    const incomingDialogSession = pendingIncomingSession ?? ((!call && !consultationCall) ? incomingSession : null);
    const incomingDialogDirection = incomingDialogSession
        ? getSessionDirection(incomingDialogSession)
        : callDirection;
    const incomingDialogCallDetailId =
        incomingDialogSession?._request?.headers?.['X-Calldetailid']?.[0]?.raw ||
        pendingUserDataCallDetails?.callDetailId ||
        pendingUserDataCallDetails?.id ||
        (!call ? (monitoringSipData?.callDetailId || monitoringSipData?.id) : null);
    const activeCallCallDetailId =
        call?._request?.headers?.['X-Calldetailid']?.[0]?.raw ||
        monitoringSipData?.callDetailId ||
        monitoringSipData?.id;
    const activeCallDirection =
        call?._request?.headers?.['X-Direction']?.[0]?.raw || monitoringSipData?.direction || callDirection;

    return (
        <div
            className={`flex flex-wrap justify-content-center ${DEV ? 'jssip-dev' : STAGE ? 'jssip-stage' : ''} jssip-tabview`}>
            {/*                {(!isOnline || strengthClass === 'weak') && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">Нестабільне інтернет з'єднання.</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            {/*                {(errorJsSip || errorJsSipTab) && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">SIP підключення не активно</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            {/*                {monitoringHubError && hash === '#/monitoring' && autoReload &&*/}
            {/*                    <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-chart-bar"></i>*/}
            {/*                                    <span className="text-lg">Помилка підключення monitoringHub.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                {statusHubError && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">Помилка підключення statusHub.</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            {/*                {userDataHubError && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">Помилка підключення userDataHub.</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            {/*                {dialerHubError && hash === '#/companies' && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">Помилка підключення dialerHub.</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            {/*                {microphoneAccess !== 'granted' && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                    <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                        <li className="layout-inline-menu-action-item m-0">*/}
            {/*                            <button className="p-link">*/}
            {/*                                <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                <span className="text-lg">Дзвінки недоступні.*/}
            {/*Відсутній доступ до мікрофона.</span>*/}
            {/*                            </button>*/}
            {/*                        </li>*/}
            {/*                    </ul>*/}
            {/*                </div>}*/}
            <Toast position="bottom-right" ref={toast}/>
            <Toast position="bottom-right" ref={toastError}/>
            <audio ref={ringtoneAudioRef} loop preload="none" src={'assets/layout/audio/ringtone.mp3'}/>
            {/*<audio ref={holdMusicAudioRef} loop src={'assets/layout/audio/onHold.mp3'}/>*/}
            <audio ref={audioRef} autoPlay={true} preload="none"></audio>
            <audio ref={endCallAudioRef} preload="none" src={'assets/layout/audio/endCall.mp3'}/>
            <audio ref={startCallAudioRef} preload="none" src={'assets/layout/audio/startCall.mp3'}/>
            <ConfirmDialog visible={confirmDialogVisible} onHide={() => setConfirmDialogVisible(false)}
                           message="Увімкнути автоматичне відтворення звуку під час дзвінка ?"
                           header="" acceptLabel="Так" rejectLabel="Запитати пізніше"
                           icon="pi pi-exclamation-triangle"
                           accept={accept}/>
            <div className="grid p-fluid max-w-22rem card">
                <div className="flex flex-column w-full field ml-3 mr-6">
                    <div className="flex w-full align-items-center mb-2" title="Гучність">
                        <div className="flex align-items-center justify-content-center"
                             style={{minWidth: `25px`, minHeight: '25px', width: '25px'}}>
                            {volume === 0 && (
                                <i className="pi pi-volume-off" style={{fontSize: '18px'}}></i>
                            )}
                            {volume > 0 && volume <= 0.5 && (
                                <i className="pi pi-volume-down" style={{fontSize: '18px'}}></i>
                            )}
                            {volume > 0.5 && (
                                <i className="pi pi-volume-up" style={{fontSize: '18px'}}></i>
                            )}
                        </div>
                        <div
                            className="sip-slider"
                            style={{'--value': volume} as React.CSSProperties}
                        >
                            <div className="sip-slider-track">
                                <div className="sip-slider-fill"/>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                className="sip-slider-input"
                                value={volume}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);

                                    setVolume(val);

                                    if (audioRef.current) {
                                        audioRef.current.volume = val;
                                    }
                                    if (holdMusicAudioRef.current) {
                                        holdMusicAudioRef.current.volume = val;
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex w-full align-items-center" title="Рингтон">
                        <div className="flex align-items-center justify-content-center"
                             style={{minWidth: `25px`, minHeight: '25px', width: '25px'}}>
                            {ringtoneVolume === 0 && (
                                <i className="pi pi-bell-slash" style={{fontSize: '18px'}}></i>
                            )}
                            {ringtoneVolume > 0 && (
                                <i className="pi pi-bell" style={{fontSize: '18px'}}></i>
                            )}
                        </div>
                        <div
                            className="sip-slider"
                            style={{'--value': ringtoneVolume} as React.CSSProperties}
                        >
                            <div className="sip-slider-track">
                                <div className="sip-slider-fill"/>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                className="sip-slider-input"
                                value={ringtoneVolume}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);

                                    setRingtoneVolume(val);

                                    if (ringtoneAudioRef.current) {
                                        ringtoneAudioRef.current.volume = val;
                                    }
                                    if (endCallAudioRef.current) {
                                        endCallAudioRef.current.volume = val;
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="field col-12 md:col-12 flex align-items-center mb-1">
                    <button onClick={() => setShowKeyboard(prevKeyboard => !prevKeyboard)}
                            className="p-button-secondary border-none h-full cursor-pointer flex align-items-center justify-content-center mr-2"
                            style={{backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px'}}>
                        <img style={{height: '20px'}} className="pnone-btns" alt='pnone-btns'
                             src={'assets/layout/images/moby.png'}/>
                    </button>
                    <AutoComplete
                        ref={autoCompleteRef}
                        value={phoneNumber}
                        itemTemplate={itemTemplate}
                        className="w-full"
                        placeholder="Введіть номер або userName"
                        panelClassName="statusNamePanel"
                        disabled={!isOnline || errorJsSip || closeCallButton || !activeSession}
                        suggestions={updatedResults}
                        completeMethod={handleInputChange}
                        onFocus={() => {
                            if (phoneNumber) {
                                autoCompleteRef.current?.show();
                            }
                        }}
                        onChange={(e) => {
                            if (e.value && e.value.userName) {
                                setPhoneNumber(e.value.userName);
                                checkUser(e.value.userName, !!call, null);
                                setQueueCallId(null);
                            } else if (e.value && e.value.name) {
                                setPhoneNumber(e.value.name);
                                setQueueCallId(e.value?.id);
                                checkUser(e.value.name, !!call, e.value?.id);
                            } else {
                                setPhoneNumber(e.value);
                            }
                        }}
                    />
                </div>
                {/* @ts-ignore: Ignoring TypeScript error for CSSTransition */}
                <CSSTransition
                    nodeRef={keyboardRef as React.RefObject<HTMLDivElement>}
                    in={showKeyboard}
                    timeout={1}
                    classNames="keyboard"
                    unmountOnExit
                >
                    <div ref={keyboardRef} className="field col-12 md:col-12 flex align-items-center mb-0">
                        <PhoneKeyboard
                            handleClickAdd={handleClickAdd}
                            handleClickDelete={handleClickDelete}
                            handleClickRemove={handleClickRemove}
                        />
                    </div>
                </CSSTransition>
                <div className="field col-12 md:col-12 flex justify-content-between flex flex-wrap mb-0">
                    {/*{<Button label="Виклик"*/}
                    {/*         disabled={errorJsSip || !activeSession || strengthClass === 'signal-icon-offline' || !isOnline || errorJsSip || closeCallButton || (call || incomingSession || consultationCall)}*/}
                    {/*         className="w-full font-bold bg-cyan-600 border-cyan-600"*/}
                    {/*         onClick={() => checkUser(phoneNumber, false, queueCallId)}></Button>}*/}
                </div>
                {(call || consultationCall) &&
                    <TabView ref={jssipTabViewRef}
                             activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}
                             className="w-full mb-2 mt-2">
                        {call && <TabPanel header="" className="w-full pt-0"
                                           headerTemplate={tab1HeaderTemplate}
                        >
                            {spySignalOn && spySessionMatchesMonitoring &&(
                                <>
                                    <div className="field col-12 md:col-12 flex mb-0 align-items-center jssip-spy-banner-pulse">
                                        <p className="font-bold mb-0 mx-2"><i className="pi pi-volume-up mr-2" style={{ fontSize: '1.5rem' }}></i></p><p className="font-bold mb-0">{isSpyStatus?.userNameSpy || isSpyStatus?.UserNameSpy || 'mykhailo.zaichenko'}</p>
                                    </div>
                                    <div className="field col-12 md:col-12 p-0"
                                         style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                                </>
                            )}
                            {call && <div className="field col-12 md:col-12 flex mb-0 align-items-center w-full">
                                <div className="flex justify-content-between w-full flex align-items-center">
                                    <div className="flex align-items-center">
                                        <p className="font-bold mb-0">{activeCallDirection}</p>
                                    </div>
                                </div>
                                {!!call?._request?.headers?.['X-Queueid']?.[0]?.raw &&
                                    <div className="ml-auto">
                                        <p className="font-bold mb-0 flex"><span
                                            className="block">ID:</span> {call?._request?.headers?.['X-Queueid']?.[0]?.raw}
                                        </p>
                                    </div>}
                                {!!call?._request?.headers?.['X-Campaignid']?.[0]?.raw && call?._request?.headers?.['X-Direction']?.[0]?.raw !== "c2c" && call?._request?.headers?.['X-Direction']?.[0].raw !== 'transfer' &&
                                    <div className="ml-auto">
                                        <p className="font-bold mb-0 flex"><span
                                            className="block">ID:</span> {call?._request?.headers?.['X-Campaignid']?.[0]?.raw}
                                        </p>
                                    </div>}
                            </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {!!call?._request?.from?._display_name && activeCallDirection !== 'transfer' && (normalizePhone(call?.remote_identity?.uri?.user) !== normalizePhone(call?._request?.from?._display_name)) &&
                                <div className="field col-12 md:col-12 flex mb-0 align-items-center">
                                    <p className="font-bold mb-0">{call?._request?.from?._display_name}</p>
                                </div>}
                            {!!call?._request?.headers?.['Referred-By']?.[0]?.raw && activeCallDirection === 'transfer' && (normalizePhone(call?.remote_identity?.uri?.user) !== normalizePhone(extractUsername(call?._request?.headers?.['Referred-By']?.[0]?.raw))) &&
                                <div className="field col-12 md:col-12 flex mb-0 align-items-center">
                                    <p className="font-bold mb-0">{extractUsername(call?._request?.headers?.['Referred-By']?.[0]?.raw)}</p>
                                </div>
                            }
                            {/*{incomingSession?._request?.headers?.['X-Calltype']?.[0]?.raw === "spycall" &&*/}
                            {/*    <div className="field col-12 md:col-12 flex mb-0 align-items-center">*/}
                            {/*        <p className="font-bold mb-0">{monitoringSipData?.ani || monitoringSipData?.ani}</p>*/}
                            {/*    </div>}*/}
                            {/*{incomingSession?._request?.headers?.['X-Calltype']?.[0]?.raw === "spycall" &&*/}
                            {/*    <div className="field col-12 md:col-12 flex mb-0 align-items-center">*/}
                            {/*        <p className="font-bold mb-0">{monitoringSipData?.ani || monitoringSipData?.ani}</p>*/}
                            {/*    </div>}*/}
                            {!!call?._request?.from?._display_name && activeCallDirection !== 'transfer' && (normalizePhone(call?.remote_identity?.uri?.user) !== normalizePhone(call?._request?.from?._display_name)) &&
                                <div className="field col-12 md:col-12 p-0"
                                     style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>}
                            {!!call?._request?.headers?.['Referred-By']?.[0]?.raw && activeCallDirection === 'transfer' && (normalizePhone(call?.remote_identity?.uri?.user) !== normalizePhone(extractUsername(call?._request?.headers?.['Referred-By']?.[0]?.raw))) &&
                                <div className="field col-12 md:col-12 p-0"
                                     style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>}
                            {call && <div className="field col-12 md:col-12 flex mb-0 align-items-center">
                                <p className="font-bold mb-0">{call?.remote_identity.uri.user}</p>
                            </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {call &&
                                <div
                                    className="col-12 md:col-12 flex justify-content-between mb-0 align-items-center">
                                    <div className="justify-content-between align-items-center">
                                        <Button icon="pi pi-clock"
                                                className="p-button-secondary flex align-items-center justify-content-center"
                                                style={{minWidth: '30px', minHeight: '30px', width: `30px`}}></Button>
                                    </div>
                                    <div style={{minWidth: '63px'}}>{formatCallDuration(callDuration)}</div>
                                </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {(!isCallHold && call?._request?.headers?.['X-Calltype']?.[0]?.raw !== "spycall") ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 pb-2 mb-0">
                                    <Button
                                        className="p-button-secondary flex align-items-center justify-content-center"
                                        style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                        onClick={holdCall}>
                                        <img style={{height: '15px'}} className="pnone-btns-phone" alt='pnone-btns1'
                                             src={'assets/layout/images/telephone-call.png'}/>
                                    </Button>
                                    <div style={{minWidth: '63px'}}>{formatCallDuration(callHoldDuration)}</div>
                                </div>) : call?._request?.headers?.['X-Calltype']?.[0]?.raw !== "spycall" ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 mb-0 pb-2">
                                    <Button
                                        className="p-button-secondary flex align-items-center justify-content-center"
                                        style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                        onClick={unHoldCall}>
                                        <img style={{height: '15px'}} alt='pnone-btns1'
                                             src={'assets/layout/images/call-red.png'}/>
                                    </Button>
                                    <div
                                        style={{color: 'red', minWidth: '63px'}}>{formatCallDuration(callHoldDuration)}</div>
                                </div>
                            ) : ''}
                            {/*{call?._request?.headers?.['X-Calltype']?.[0]?.raw !== "spycall" &&*/}
                            {showMuteCallControls &&
                                <div className="field col-12 md:col-12 p-0"
                                     style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>}
                            {/*{(!isCallMute && incomingSession?._request?.headers?.['X-Calltype']?.[0]?.raw !== "spycall") ? (*/}
                            {!isCallMute && showMuteCallControls ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 pb-2 mb-0">
                                    <Button icon="pi pi-microphone"
                                            className="p-button-secondary flex align-items-center justify-content-center"
                                            style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                            onClick={muteAudio}>
                                    </Button>
                                    <div style={{minWidth: '63px'}}>{formatCallDuration(callMuteDuration)}</div>
                                </div>) : showMuteCallControls ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 mb-0 pb-2">
                                    <Button icon="pi pi-microphone"
                                            className="p-button-secondary flex align-items-center justify-content-center"
                                            style={{minWidth: '30px', minHeight: '30px', width: `30px`, color: 'red'}}
                                            onClick={unmuteAudio}>
                                    </Button>
                                    <div
                                        style={{color: 'red', minWidth: '63px'}}>{formatCallDuration(callMuteDuration)}</div>
                                </div>
                            ) : ''}
                            {showMuteCallControls &&
                                <div className="field col-12 md:col-12 p-0 mb-0"
                                     style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>}
                            <div
                                className="field col-12 md:col-12 flex justify-content-between incomingDialog flex flex-wrap px-0 mr-1 mb-0">
                                <div className="field col-12 md:col-12 pl-0 mb-0 flex justify-content-between">
                                    {activeCallCallDetailId &&
                                        <Button
                                            className="font-bold button-primary justify-content-center decline-call w-full mb-0 decline-call border-round-xl max-w-5rem"
                                            onClick={() => openDetails(activeCallCallDetailId)}
                                            style={{minWidth: '60px', minHeight: '40px'}}><i
                                            className="pi pi-info-circle"
                                            style={{fontSize: '1.3rem'}}></i></Button>}
                                    {/*<Button icon="pi pi-arrow-right-arrow-left"*/}
                                    {/*        className="font-bold text-xl border-none shadow-none justify-content-center w-full border-round-xl decline-call mb-0 max-w-5rem"*/}
                                    {/*        style={{minWidth: '60px', minHeight: '40px', backgroundColor: '#873efe'}}></Button>*/}
                                    {consultationCall && <Button
                                        onClick={() => {
                                            handleReferToConsultationCall(consultationSipData?.aNI || consultationSipData?.ani || consultationCall?.remote_identity.uri.user);
                                        }}
                                        className={`font-bold justify-content-center w-full mb-0 shadow-none outline-none border-round-xl border-none ${activeCallCallDetailId ? 'max-w-5rem' : 'max-w-full mr-1'}`}
                                        icon={'pi pi-phone'}
                                        style={{backgroundColor: 'rgb(127, 72, 255)'}}><img className="arrow-refer"
                                                                                            style={{
                                                                                                width: '13px',
                                                                                                left: '56%',
                                                                                                transform: 'translate(-55%, 0)',
                                                                                                top: '9px'
                                                                                            }}
                                                                                            src={"assets/layout/images/arrow-right.png"}
                                                                                            alt="avatar"/>
                                    </Button>}
                                    {call?._request?.headers?.['X-Calltype']?.[0]?.raw === "spycall" &&
                                        <Button
                                            onClick={() => {
                                                handleToggleSpyCallMode();
                                            }}
                                            className={`font-bold iconIconSpy justify-content-center w-full mb-0 shadow-none outline-none border-round-xl border-none ${activeCallCallDetailId ? 'max-w-5rem' : 'max-w-full mr-1'}`}
                                            icon={spyCallMode === "listen" ? "pi pi-volume-up" : "pi pi-headphones"}
                                            style={{
                                                backgroundColor: spyCallMode === "listen"
                                                    ? 'rgb(127, 72, 255)'
                                                    : 'rgb(253, 166, 16)'
                                            }}
                                        >{spyCallMode !== "listen" && <img className="arrow-refer"
                                              style={{
                                                  width: '13px',
                                                  left: '70%',
                                                  transform: ' translate(-54%, 0px)',
                                                  top: '12px'
                                              }}
                                              src={"assets/layout/images/arrow-right.png"}
                                              alt="avatar"/>}
                                        </Button>}
                                    <Button
                                        className={`font-bold p-button-danger justify-content-center w-full border-round-xl decline-call mb-0 ${activeCallCallDetailId ? 'max-w-5rem' : consultationCall ? 'max-w-full ml-1' : 'max-w-full'}`}
                                        onClick={() => endCall(true)} style={{minWidth: '60px', minHeight: '40px'}}><i
                                        className="pi pi-phone" style={{fontSize: '1.3rem'}}></i>
                                    </Button>
                                </div>
                                {/*<div className="field col-4 md:col-4 px-1 mb-0">*/}
                                {/*</div>*/}
                                {/*<div className="field col-4 md:col-4 pr-0 mb-0">*/}
                                {/*    <Button*/}
                                {/*        className="font-bold p-button-danger justify-content-center w-full border-round-xl decline-call mb-0"*/}
                                {/*        onClick={endCall} style={{minWidth: '60px', minHeight: '40px'}}><i className="pi pi-phone" style={{fontSize: '1.3rem'}}></i>*/}
                                {/*    </Button>*/}
                                {/*</div>*/}
                            </div>
                        </TabPanel>}
                        {consultationCall && <TabPanel header="" className="w-full pt-0"
                                                       headerTemplate={consultationHeaderTemplate}
                        >
                            {spySignalOn && spySessionMatchesConsultation && (
                                <>
                                    <div className="field col-12 md:col-12 flex mb-0 align-items-center jssip-spy-banner-pulse">
                                        <p className="font-bold mb-0 mx-2"><i className="pi pi-volume-up mr-2" style={{ fontSize: '1.5rem' }}></i></p><p className="font-bold mb-0">{isSpyStatus?.userNameSpy || isSpyStatus?.UserNameSpy || '- - -'}</p>
                                    </div>
                                    <div className="field col-12 md:col-12 p-0"
                                         style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                                </>
                            )}
                            {consultationCall &&
                                <div className="field col-12 md:col-12 flex mb-0 align-items-center w-full">
                                    <div className="flex justify-content-between w-full flex align-items-center">
                                        <div className="flex align-items-center">
                                            <p className="font-bold mb-0">{consultationCall?._request?.headers?.['X-Direction']?.[0]?.raw || consultationSipData?.direction || '- - -'}</p>
                                        </div>
                                    </div>
                                </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {consultationCall &&
                                <div className="field col-12 md:col-12 flex mb-0 align-items-center">
                                    <p className="font-bold mb-0">{consultationCall?.remote_identity.uri.user}</p>
                                </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {consultationCall &&
                                <div
                                    className="col-12 md:col-12 flex justify-content-between mb-0 align-items-center">
                                    <div className="justify-content-between align-items-center">
                                        <Button icon="pi pi-clock"
                                                className="p-button-secondary flex align-items-center justify-content-center"
                                                style={{minWidth: '30px', minHeight: '30px', width: `30px`}}></Button>
                                    </div>
                                    <div
                                        style={{minWidth: '63px'}}>{formatCallDuration(consultationCallDuration)}</div>
                                </div>}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {!isConsultationCallHold ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 pb-2 mb-0">
                                    <Button
                                        className="p-button-secondary flex align-items-center justify-content-center"
                                        style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                        onClick={holdConsultationCall}>
                                        <img className="pnone-btns-phone" style={{height: '15px'}} alt='pnone-btns1'
                                             src={'assets/layout/images/telephone-call.png'}/>
                                    </Button>
                                    <div
                                        style={{minWidth: '63px'}}>{formatCallDuration(consultationCallHoldDuration)}</div>
                                </div>) : (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 mb-0 pb-2">
                                    <Button
                                        className="p-button-secondary flex align-items-center justify-content-center"
                                        style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                        onClick={unHoldConsultationCall}>
                                        <img style={{height: '15px'}} alt='pnone-btns1'
                                             src={'assets/layout/images/call-red.png'}/>
                                    </Button>
                                    <div
                                        style={{color: 'red', minWidth: '63px'}}>{formatCallDuration(consultationCallHoldDuration)}</div>
                                </div>
                            )}
                            <div className="field col-12 md:col-12 p-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            {!isConsultationCallMute ? (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 pb-2 mb-0">
                                    <Button icon="pi pi-microphone"
                                            className="p-button-secondary flex align-items-center justify-content-center"
                                            style={{minWidth: '30px', minHeight: '30px', width: `30px`}}
                                            onClick={muteConsultationAudio}>
                                    </Button>
                                    <div
                                        style={{minWidth: '63px'}}>{formatCallDuration(consultationCallMuteDuration)}</div>
                                </div>) : (
                                <div
                                    className="flex field justify-content-between align-items-center pl-2 pr-2 pt-2 mb-0 pb-2">
                                    <Button icon="pi pi-microphone"
                                            className="p-button-secondary flex align-items-center justify-content-center"
                                            style={{minWidth: '30px', minHeight: '30px', width: `30px`, color: 'red'}}
                                            onClick={unmuteConsultationAudio}>
                                    </Button>
                                    <div
                                        style={{color: 'red', minWidth: '63px'}}>{formatCallDuration(consultationCallMuteDuration)}</div>
                                </div>
                            )}
                            <div className="field col-12 md:col-12 p-0 mb-0"
                                 style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                            <div
                                className="field col-12 md:col-12 flex justify-content-between incomingDialog flex flex-wrap px-0 mr-1 mb-0">
                                <div className="field col-12 md:col-12 pl-0 mb-0 flex justify-content-between">
                                    {(consultationSipData?.id || consultationSipData?.callDetailId) &&
                                        <Button
                                            className="font-bold button-primary justify-content-center decline-call w-full mb-0 decline-call border-round-xl max-w-5rem"
                                            onClick={() => openDetails((consultationSipData?.id || consultationSipData?.callDetailId), true)}
                                            style={{minWidth: '60px', minHeight: '40px'}}><i
                                            className="pi pi-info-circle"
                                            style={{fontSize: '1.3rem'}}></i></Button>}
                                    {consultationCall && <Button
                                        onClick={() => {
                                            handleReferToConsultationCall(consultationSipData?.aNI || consultationSipData?.ani || consultationCall?.remote_identity.uri.user);
                                        }}
                                        className={`font-bold justify-content-center w-full mb-0 shadow-none outline-none border-round-xl border-none ${(consultationSipData?._request?.headers?.['X-Calldetailid']?.[0]?.raw || consultationSipData?.id || consultationSipData?.callDetailId) ? 'max-w-5rem' : 'max-w-full mr-1'}`}
                                        icon={'pi pi-phone'}
                                        style={{backgroundColor: 'rgb(127, 72, 255)'}}><img className="arrow-refer"
                                                                                            style={{
                                                                                                width: '13px',
                                                                                                left: '56%',
                                                                                                transform: 'translate(-55%, 0)',
                                                                                                top: '9px'
                                                                                            }}
                                                                                            src={"assets/layout/images/arrow-right.png"}
                                                                                            alt="avatar"/>
                                    </Button>}
                                    <Button
                                        className={`font-bold p-button-danger justify-content-center w-full border-round-xl decline-call mb-0 ${(consultationSipData?.id || consultationSipData?.callDetailId) ? 'max-w-5rem' : 'max-w-full'}`}
                                        onClick={() => endConsultationCall(true)}
                                        style={{minWidth: '60px', minHeight: '40px'}}><i
                                        className="pi pi-phone" style={{fontSize: '1.3rem'}}></i>
                                    </Button>
                                </div>
                            </div>
                        </TabPanel>}
                    </TabView>}

                {showDialog && incomingDialogSession && <div className="flex field col-12 p-fluid max-w-22rem mb-2 px-0">
                    <div className="field col-12 md:col-12 flex flex-column align-items-center mb-1 card">
                        <p className="font-bold text-2xl text-green-500 mb-1">Вхідний дзвінок</p>
                        <div className="flex align-items-center w-full mb-2">
                            <div className="flex align-items-center justify-content-around w-full">
                                <div className="flex align-items-center">
                                    <div className="relative">
                                        {incomingDialogSession?._request?.headers?.['X-Direction']?.[0].raw === 'transfer' ?
                                            <i className="pi pi-reply p-2 bg-green-500 border-circle text-white"
                                               style={{transform: 'rotate(90deg) scaleX(1) scaleY(-1)'}}></i> :
                                            incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'consultationInternal' ? (
                                                <div className="relative">
                                                    <i className="pi pi-users p-2 border-circle text-white"
                                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                                        <div className="border-circle relative bg-green-500"
                                                             style={{width: '14px', height: '14px'}}>
                                                            <i className="pi pi-arrow-down-left text-white absolute left-50 top-50 kk"
                                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'callbackExternal' ? (
                                                <div className="relative">
                                                    <i className="pi pi-undo p-2 border-circle text-white"
                                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                                        <div className="border-circle relative bg-primary"
                                                             style={{width: '14px', height: '14px'}}>
                                                            <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                                        </div>
                                                    </div>
                                                </div>) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'callbackInternal' ? (
                                                <div className="relative">
                                                    <i className="pi pi-undo p-2 border-circle text-white"
                                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                                        <div className="border-circle relative bg-green-500"
                                                             style={{width: '14px', height: '14px'}}>
                                                            <i className="pi pi-arrow-down-left text-white absolute left-50 top-50 kk"
                                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                                        </div>
                                                    </div>
                                                </div>) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'consultationExternal' ? (
                                                <div className="relative">
                                                    <i className="pi pi-users p-2 border-circle text-white"
                                                       style={{background: 'rgb(253, 166, 16)'}}></i>
                                                    <div className="absolute" style={{top: '-5px', left: '18px'}}>
                                                        <div className="border-circle relative bg-primary"
                                                             style={{width: '14px', height: '14px'}}>
                                                            <i className="pi pi-arrow-up-right text-white absolute left-50 top-50 kk"
                                                               style={{fontSize: '10px', transform: 'translate(-50%, -50%)'}}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === "transferInternal" ? (
                                                <div className="relative">
                                                    <i className="pi pi-reply p-2 bg-green-500 border-circle text-white"
                                                       style={{transform: 'rotate(90deg) scaleX(1) scaleY(-1)'}}></i>
                                                </div>
                                            ) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'external' ? (
                                                <div className="relative">
                                                    <i className="pi pi-arrow-up-right bg-primary p-2 border-circle text-white"></i>
                                                </div>
                                            ) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'ivm' ? (
                                                <span
                                                    className="inline-flex shrink-0 align-items-center justify-content-center bg-primary text-white font-bold border-circle"
                                                    style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVM</span>
                                            ) : incomingDialogSession?._request?.headers?.['X-Calltype']?.[0].raw === 'ivr' ? (
                                                    <span
                                                        className="inline-flex shrink-0 align-items-center justify-content-center bg-green-500 text-white font-bold border-circle"
                                                        style={{fontStyle: "normal", width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', borderRadius: '50%', fontSize: '11px'}}>IVR</span>
                                                ) :
                                                <i className="pi pi-arrow-down-left p-2 border-circle bg-green-500 text-white"></i>}
                                    </div>
                                    <p className="font-bold mb-0 ml-2">{incomingDialogDirection}</p>
                                </div>
                                {!!incomingDialogSession?._request?.headers?.['X-Queueid']?.[0]?.raw &&
                                    <div className="ml-auto">
                                        <p className="font-bold mb-0 flex"><span
                                            className="block">ID:</span> {incomingDialogSession?._request?.headers?.['X-Queueid']?.[0]?.raw}
                                        </p>
                                    </div>}
                                {!!incomingDialogSession?._request?.headers?.['X-Campaignid']?.[0]?.raw && incomingDialogSession?._request?.headers?.['X-Direction']?.[0].raw !== 'transfer' &&
                                    <div className="ml-auto">
                                        <p className="font-bold mb-0 flex"><span
                                            className="block">ID:</span> {incomingDialogSession?._request?.headers?.['X-Campaignid']?.[0]?.raw}
                                        </p>
                                    </div>}
                            </div>
                        </div>
                        {!!incomingDialogSession?._request?.from?._display_name && incomingDialogDirection !== 'transfer' && (normalizePhone(incomingDialogSession?.remote_identity?.uri?.user) !== normalizePhone(incomingDialogSession?._request?.from?._display_name)) &&
                            <div>
                                <p className="font-bold text-xl">{incomingDialogSession?._request?.from?._display_name}</p>
                            </div>}
                        {!!incomingDialogSession?._request?.headers?.['Referred-By']?.[0]?.raw && incomingDialogDirection === 'transfer' && (normalizePhone(incomingDialogSession?.remote_identity?.uri?.user) !== normalizePhone(extractUsername(incomingDialogSession?._request?.headers?.['Referred-By']?.[0]?.raw))) &&
                            <div>
                                <p className="font-bold text-xl">{extractUsername(incomingDialogSession?._request?.headers?.['Referred-By']?.[0]?.raw)}</p>
                            </div>}
                        <p className="font-bold text-xl mb-0 text-center">{incomingDialogSession?.remote_identity?.uri?.user}</p>

                        <div className="field col-12 md:col-12 p-0 my-3"
                             style={{margin: 0, borderBottom: '1px solid #cccccc38'}}></div>
                        <div className="incomingDialog flex justify-content-between">
                            {incomingDialogCallDetailId &&
                                <Button
                                    className="font-bold button-primary justify-content-center decline-call w-full mb-0 decline-call border-round-2xl max-w-5rem"
                                    onClick={() => openDetails(incomingDialogCallDetailId)}
                                    style={{minWidth: '60px', minHeight: '40px'}}><i className="pi pi-info-circle"
                                                                                     style={{fontSize: '1.3rem'}}></i></Button>}
                            <Button icon="pi pi-phone"
                                    className="font-bold justify-content-center border-round-2xl mx-4 answer-call shadow-none"
                                    onClick={acceptCall}
                                    style={{minWidth: '60px', minHeight: '40px', backgroundColor: '#369b26', borderColor: '#369b26'}}></Button>
                            <Button icon="pi pi-phone"
                                    className="font-bold p-button-danger justify-content-center border-round-2xl decline-call"
                                    onClick={declineCall} style={{minWidth: '60px', minHeight: '40px'}}></Button>
                            {/*<Button*/}
                            {/*    className="font-bold p-button-danger bg-orange-400 border-orange-400 justify-content-center border-round-2xl mute-call"*/}
                            {/*    style={{minWidth: '60px', minHeight: '40px', maxWidth: '40px'}} onClick={muteCall}>*/}
                            {/*    <img style={{width: '17px'}} alt='Mute icons created by Google - Flaticon'*/}
                            {/*         src={'assets/layout/images/volume-off.png'}/>*/}
                            {/*</Button>*/}
                        </div>
                    </div>
                </div>}
                {selectedRowData && activeCallCallDetailId && visible && activeIndex === 0 && (call || (incomingSession && !pendingIncomingSession) || userStatusObj?.statusesId === 11) &&
                    <MonitoringSipDetails setVisible={closeDetailVisible}/>
                }
                {selectedConsultationData && (consultationSipData?.callDetailId || consultationSipData?.id) && visibleConsultation && activeIndex === 1 && consultationCall &&
                    <ConsultationSipDetails setVisible={closeConsultationDetails}/>
                }
            </div>
            {/*                <div className="flex flex-column">*/}
            {/*                    {(!isOnline || strengthClass === 'weak') && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">Нестабільне інтернет з'єднання.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                    {(errorJsSip || errorJsSipTab) && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">SIP підключення не активно</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                    {monitoringHubError && hash === '#/monitoring' && autoReload &&*/}
            {/*                        <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                            <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                                <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                    <button className="p-link">*/}
            {/*                                        <i style={{color: "red"}} className="pi pi-info-circle pi-chart-bar"></i>*/}
            {/*                                        <span className="text-lg">Помилка підключення monitoringHub.</span>*/}
            {/*                                    </button>*/}
            {/*                                </li>*/}
            {/*                            </ul>*/}
            {/*                        </div>}*/}
            {/*                    {statusHubError && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">Помилка підключення statusHub.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                    {userDataHubError && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">Помилка підключення userDataHub.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                    {dialerHubError && hash === '#/companies' && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">Помилка підключення dialerHub.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                    {microphoneAccess !== 'granted' && <div className="layout-inline-menu m-0 mb-4 w-full">*/}
            {/*                        <ul className="layout-inline-menu-action-panel m-0">*/}
            {/*                            <li className="layout-inline-menu-action-item m-0">*/}
            {/*                                <button className="p-link">*/}
            {/*                                    <i style={{color: "red"}} className="pi pi-info-circle pi-fw"></i>*/}
            {/*                                    <span className="text-lg">Дзвінки недоступні.*/}
            {/*Відсутній доступ до мікрофона.</span>*/}
            {/*                                </button>*/}
            {/*                            </li>*/}
            {/*                        </ul>*/}
            {/*                    </div>}*/}
            {/*                </div>*/}
        </div>
    );
}
// @ts-ignore
export default SIPCallComponent;
